// ============================================================
// assets/2609-event-mgr.js - September Event Manager
// ============================================================

/**
 * 0. Utility: Get today's date in KST (Korea Standard Time)
 */
function getTodayKST() {
    const now = new Date();
    // Convert to KST (UTC+9)
    const kstOffset = 9 * 60; // 9 hours in minutes
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const kstTime = new Date(utcTime + (kstOffset * 60000));

    const year = kstTime.getFullYear();
    const month = String(kstTime.getMonth() + 1).padStart(2, '0');
    const day = String(kstTime.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * 1. Backend API Client
 */
class EventApiClient {
    constructor(customerId, eventId) {
        this.baseUrl = (window.routes && window.routes.api_url) ? window.routes.api_url : '';
        this.customerId = String(customerId);
        this.eventId = eventId;
    }

    async _fetch(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const timeout = options.timeout || 30000; // 기본 30초 타임아웃
        const config = {
            mode: 'cors',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        // 타임아웃 처리
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        config.signal = controller.signal;

        try {
            const res = await fetch(url, config);
            clearTimeout(timeoutId);

            // JSON 파싱 실패 대비
            let json;
            try {
                json = await res.json();
            } catch (parseErr) {
                console.error(`[API Parse Error] ${endpoint}:`, parseErr);
                throw new Error('서버 응답 형식이 올바르지 않습니다.');
            }

            if (!res.ok) {
                const errorMessage = json.message || json.error || `API Error ${res.status}`;
                console.error(`[API Error] ${endpoint}:`, errorMessage, json);
                throw new Error(errorMessage);
            }

            return json;
        } catch (err) {
            clearTimeout(timeoutId);

            // 네트워크 오류 타입별 메시지
            if (err.name === 'AbortError') {
                console.error(`[API Timeout] ${endpoint}:`, err);
                throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
            } else if (err.message === '서버 응답 형식이 올바르지 않습니다.') {
                throw err;
            } else if (!navigator.onLine) {
                console.error(`[API Network Error] ${endpoint}:`, err);
                throw new Error('네트워크 연결을 확인해주세요.');
            }

            console.error(`[API Fail] ${endpoint}:`, err);
            throw err;
        }
    }

    async checkIn(missionId = 'mission_attendance') {
        return this._fetch('/event/check-in', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                missionId: missionId
            })
        });
    }

    async claimFreeCoins() {
        return this._fetch('/event/claim-free-coins', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId
            })
        });
    }

    async exchangeReward(rewardId) {
        return this._fetch('/event/reward-exchange', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                rewardId: rewardId
            })
        });
    }

    async getHistory(limit = 1, lastKey = null) {
        const params = new URLSearchParams({
            customerId: this.customerId,
            eventId: this.eventId,
            limit: limit
        });
        if (lastKey) params.append('lastKey', lastKey);
        return this._fetch(`/event/history?${params.toString()}`, { method: 'GET' });
    }

    async webhook(missionId) {
        return this._fetch('/event/webhook', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                missionId: missionId
            })
        });
    }
}

/**
 * 2. Event State Manager
 */
class EventStateManager {
    constructor() {
        this.state = {
            isLoggedIn: false,
            config: {
                settings: {},
                missions: [],
                rewards: [],
                gacha: {}
            },
            user: {
                id: null,
                currentStamps: 0,
                freeCoinsToday: 0,
                claimedRewards: [],
                attendanceHistory: [],
                dailyFreeCoinHistory: [],
                specialFreeBonusHistory: [],
                viewRecommendedProductsHistory: [],
                freeCoinDates: [],
                missionCounts: {
                    attendance: 0,
                    order: 0,
                    review: 0,
                    bonusAll: 0,
                    bonusOrder: 0,
                    signup: 0,
                    luckyDrop: 0
                }
            }
        };
    }

    _parseJson(elementId) {
        const el = document.getElementById(elementId);
        if (!el) return null;
        try { return JSON.parse(el.textContent); }
        catch (e) { return null; }
    }

    initialize(ids) {
        this.state.config.settings = this._parseJson(ids.settingsId) || {};
        this.state.config.missions = this._parseJson(ids.missionsId) || [];
        this.state.config.rewards = this._parseJson(ids.rewardsId) || [];

        // Fallback
        if (this.state.config.rewards.length === 0) {
            this.state.config.rewards = [
                { id: 'reward_shipping', cost_stamps: 5, limit_per_user: 1 },
                { id: 'reward_product_100', cost_stamps: 10, limit_per_user: 1 },
                { id: 'reward_coupon_20k', cost_stamps: 15, limit_per_user: 1 },
                { id: 'reward_coupon_30k', cost_stamps: 30, limit_per_user: 1 }
            ];
        }

        this.state.config.gacha = this._parseJson(ids.gachaId) || {};

        const rawCustomerData = this._parseJson(ids.customerDataId);
        const eventHandle = ids.eventHandle;

        if (rawCustomerData && rawCustomerData.customer_id) {
            this.state.isLoggedIn = true;
            this.state.user.id = rawCustomerData.customer_id;

            const eventHistory = rawCustomerData.events && rawCustomerData.events[eventHandle];
            if (eventHistory) {
                this.state.user.currentStamps = eventHistory.current_stamps || eventHistory.total_stamps || 0;
                this.state.user.freeCoinsToday = eventHistory.free_coins_today || 0;
                this.state.user.claimedRewards = eventHistory.rewards?.claimed_ids || [];
                this.state.user.freeCoinDates = eventHistory.free_coin_dates || [];

                if (eventHistory.mission_progress) {
                    const missionProgress = eventHistory.mission_progress;
                    const attendance = missionProgress.mission_attendance || missionProgress.attendance || {};
                    const dailyFreeCoins = missionProgress.daily_free_coins || {};
                    const specialFreeBonus = missionProgress.special_free_bonus || {};
                    const viewRecommendedProducts = missionProgress.view_recommeded_products || {};
                    const order = missionProgress.mission_order || {};
                    const review = missionProgress.mission_review || {};
                    const bonusAll = missionProgress.all_missions_complete_bonus || missionProgress.mission_bonus_all || {};
                    const bonusOrder = missionProgress.mission_bonus_order || {};
                    const signup = missionProgress.mission_signup || {};

                    this.state.user.attendanceHistory = attendance.history || [];
                    this.state.user.dailyFreeCoinHistory = dailyFreeCoins.history || [];
                    this.state.user.specialFreeBonusHistory = specialFreeBonus.history || [];
                    this.state.user.viewRecommendedProductsHistory = viewRecommendedProducts.list || [];
                    this.state.user.missionCounts.attendance = Number(attendance.count || 0);
                    this.state.user.missionCounts.order = Number(order.count || 0);
                    this.state.user.missionCounts.review = Number(review.count || 0);
                    this.state.user.missionCounts.bonusAll = Number(bonusAll.count || 0);
                    this.state.user.missionCounts.bonusOrder = Number(bonusOrder.count || 0);
                    this.state.user.missionCounts.signup = Number(signup.count || 0);
                }

                const specialBonuses = eventHistory.special_bonuses || {};
                const luckyDropCount = (specialBonuses.count !== undefined)
                    ? Number(specialBonuses.count || 0)
                    : (Array.isArray(specialBonuses.list) ? specialBonuses.list.length : 0);
                this.state.user.missionCounts.luckyDrop = Number(luckyDropCount || 0);
            }
        }
    }

    get stamps() {
        return (this.state.user.currentStamps || 0) + (this.state.user.freeCoinsToday || 0);
    }
    getRewards() { return this.state.config.rewards; }
    getRewardById(rewardId) { return this.state.config.rewards.find(r => r.id == rewardId); }

    canRedeem(rewardId) {
        if (rewardId === 'reward_gacha') {
            const gachaCost = this.state.config.gacha ? (this.state.config.gacha.cost_stamps || 1) : 1;
            if (this.stamps < gachaCost) return { possible: false, reason: `코인이 부족합니다. (필요: ${gachaCost}, 보유: ${this.stamps})` };
            return { possible: true };
        }

        const reward = this.getRewardById(rewardId);
        if (!reward) return { possible: false, reason: "존재하지 않는 상품입니다." };
        if (this.stamps < reward.cost_stamps) return { possible: false, reason: `코인이 부족합니다. (필요: ${reward.cost_stamps}, 보유: ${this.stamps})` };
        if (this.state.user.claimedRewards.includes(String(rewardId))) return { possible: false, reason: "이미 교환한 선물입니다." };
        return { possible: true };
    }

    updateUserBalance(newBalance) { this.state.user.currentStamps = newBalance; }
    addClaimedReward(rewardId) {
        const idStr = String(rewardId);
        if (!this.state.user.claimedRewards.includes(idStr)) this.state.user.claimedRewards.push(idStr);
    }
    addAttendanceToday() {
        const today = getTodayKST();
        if (!this.state.user.attendanceHistory.includes(today)) {
            this.state.user.attendanceHistory.push(today);
            this.state.user.missionCounts.attendance += 1;
        }
    }
}

/**
 * 3. Event Controller
 */
class EventController {
    constructor(config = {}) {
        this.store = new EventStateManager();
        this.api = null;
        this.config = {
            eventHandle: '2026-6th-event',
            settingsId: 'liquid-settings-data',
            missionsId: 'liquid-missions-data',
            rewardsId: 'liquid-rewards-data',
            gachaId: 'liquid-gacha-data',
            customerDataId: 'liquid-customer-data',
            ...config
        };
    }

    init() {
        this.store.initialize(this.config);
        if (this.store.state.isLoggedIn) {
            this.api = new EventApiClient(this.store.state.user.id, this.config.eventHandle);
        }
    }

    async tryCheckIn(missionId = 'mission_attendance') {
        if (!this.api) {
            alert("로그인이 필요합니다.");
            return false;
        }
        try {
            const res = await this.api.checkIn(missionId);

            // special_free_bonus는 다른 응답 구조를 가짐
            // { message, stamps_added: 0, coin_type: "free", new_balance: null }
            if (missionId === 'special_free_bonus') {
                if (res.message && res.coin_type === 'free') {
                    // 오늘 날짜를 specialFreeBonusHistory에 추가
                    const today = getTodayKST();
                    if (!this.store.state.user.specialFreeBonusHistory.includes(today)) {
                        this.store.state.user.specialFreeBonusHistory.push(today);
                    }
                    // event/history를 호출해서 복주머니 카운트 업데이트 필요
                    // fetchLatestStatus()에서 처리됨
                    return true;
                } else {
                    alert(res.message || "이미 받았습니다.");
                    return false;
                }
            }

            // 일반 응답 구조: { message, stamps_added, new_balance }
            if (res.new_balance !== undefined) {
                this.store.updateUserBalance(res.new_balance);

                // mission_attendance일 때 출석 기록 추가
                if (missionId === 'mission_attendance') {
                    this.store.addAttendanceToday();
                }

                // daily_free_coins일 때 황금 복주머니 받기 기록 추가
                if (missionId === 'daily_free_coins') {
                    const today = getTodayKST();
                    if (!this.store.state.user.dailyFreeCoinHistory.includes(today)) {
                        this.store.state.user.dailyFreeCoinHistory.push(today);
                    }
                }

                return true;
            } else {
                alert(res.message || "이미 출석했습니다.");
                return false;
            }
        } catch (e) {
            console.error('[tryCheckIn] Error:', e);
            alert("출석체크 중 오류가 발생했습니다.");
            return false;
        }
    }

    async tryClaimFreeCoins() {
        if (!this.api) {
            alert("로그인이 필요합니다.");
            return { success: false };
        }
        try {
            const res = await this.api.claimFreeCoins();
            if (res.success) {
                // 새 잔액 업데이트 (free_coins_balance 또는 total_stamps)
                const newBalance = res.free_coins_balance;
                if (newBalance !== undefined) {
                    this.store.updateUserBalance(newBalance);
                }

                // 오늘 날짜를 dailyFreeCoinHistory에 추가
                const today = getTodayKST();
                if (!this.store.state.user.dailyFreeCoinHistory.includes(today)) {
                    this.store.state.user.dailyFreeCoinHistory.push(today);
                }

                return { success: true, coinsGranted: res.free_coins_granted || 0 };
            } else {
                alert(res.message || "이미 보너스 코인을 받았습니다.");
                return { success: false };
            }
        } catch (e) {
            console.error('[tryClaimFreeCoins] Error:', e);
            alert("보너스 코인 수령 중 오류가 발생했습니다.");
            return { success: false };
        }
    }

    async tryExchange(rewardId, isGacha = false) {
        if (!this.api) {
            alert("로그인이 필요합니다.");
            return false;
        }

        const check = this.store.canRedeem(rewardId);
        if (!check.possible) {
            alert(check.reason);
            return false;
        }

        try {
            const res = await this.api.exchangeReward(rewardId);
            const resultPayload = (res && res.result && typeof res.result === "object") ? res.result : {};
            const isSuccess = (res && res.success === true) || (resultPayload && resultPayload.success === true);

            if (isSuccess) {
                if (!isGacha) {
                    this.store.addClaimedReward(rewardId);
                }

                if (isGacha) {
                    return { success: true, data: resultPayload };
                } else {
                    const modal = document.querySelector('.redeem_popup[data-popup="redeem_result"]');
                    const titleEl = modal?.querySelector('[data-role="title"]');
                    const descEl = modal?.querySelector('[data-role="desc"]');
                    const reward = this.store.getRewardById(rewardId);
                    const cost = reward ? reward.cost_stamps : 0;
                    const rewardTitle = resultPayload.title || (res && res.title) || "선물";

                    if (titleEl) titleEl.innerText = `${rewardTitle} 발급 완료`;
                    if (descEl) descEl.innerHTML = `코인 ${cost}개로 ${rewardTitle} 교환을<br>완료하였습니다`;
                    if (modal) $(modal).show();
                    return true;
                }
            } else {
                const errorMessage = res.message || "교환에 실패했습니다.";
                alert(errorMessage);
                return false;
            }
        } catch (e) {
            console.error('[tryExchange] Error:', e);
            const errorMessage = e.message || "일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
            alert(errorMessage);
            return false;
        }
    }

    async triggerWebhook(missionId) {
        console.log('[triggerWebhook] Called with missionId:', missionId);
        if (!this.api) {
            console.warn('[triggerWebhook] API not initialized');
            return false;
        }
        try {
            console.log('[triggerWebhook] Calling API webhook...');
            const res = await this.api.webhook(missionId);
            console.log('[triggerWebhook] API response:', res);

            // 응답이 정상적으로 왔으면 성공 (HTTP 202 OK)
            // res.success가 명시적으로 false가 아니면 성공으로 처리
            const success = res.success !== false;
            console.log('[triggerWebhook] Success value:', success);

            // view_recommeded_products 성공 시 오늘 날짜를 기록
            if (success && missionId === 'view_recommeded_products') {
                const today = getTodayKST();
                console.log('[triggerWebhook] Adding today to history:', today);
                if (!this.store.state.user.viewRecommendedProductsHistory.includes(today)) {
                    this.store.state.user.viewRecommendedProductsHistory.push(today);
                }
            }

            return success;
        } catch (e) {
            console.error('[triggerWebhook] Error:', e);
            return false;
        }
    }
}

/**
 * 4. Main Page Logic with jQuery UI Integration
 */
$(function () {
    // =========================================================
    // ✅ 코인 히스토리 렌더링
    // =========================================================
    const renderHistory = (historyList) => {
        const tbody = document.querySelector('.history_table--body tbody');
        if (!tbody) return;
        tbody.querySelectorAll('.history_row').forEach(row => row.remove());
        const emptyMsg = tbody.querySelector('.history_empty');

        if (!historyList || historyList.length === 0) {
            if(emptyMsg) emptyMsg.style.display = 'table-row';
        } else {
            if(emptyMsg) emptyMsg.style.display = 'none';
            const baseUrl = "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc";

            historyList.forEach(item => {
                const tr = document.createElement('tr');
                tr.className = 'history_row';
                const date = item.timestamp ? item.timestamp.split('T')[0] : (item.created_at ? item.created_at.split('T')[0] : '-');
                const absAmount = Math.abs(item.amount);
                let coinDisplay = `${absAmount}`;
                let badgeType = 'type_exchange';
                let badgeText = '사용';
                let imgSrc = `${baseUrl}/gift_box.png`;

                if (item.type === 'gacha_redeem') {
                    badgeType = 'type_gacha'; badgeText = '가챠'; imgSrc = `${baseUrl}/lucky_ball.png`;
                } else if (item.type === 'redeem') {
                    badgeType = 'type_exchange'; badgeText = '교환'; imgSrc = `${baseUrl}/gift_box.png`;
                } else if (item.amount > 0) {
                    badgeType = 'type_gacha'; badgeText = '적립'; imgSrc = `${baseUrl}/coin_mini.png`; coinDisplay = `+${absAmount}`;
                }

                tr.innerHTML = `
                    <td class="td_info">
                        <div class="item_img"><img src="${imgSrc}" alt="" /></div>
                        <div class="item_text">
                            <span class="item_badge ${badgeType}">${badgeText}</span>
                            <span class="item_name">${item.description || item.title || '이벤트 참여'}</span>
                        </div>
                    </td>
                    <td class="td_date">${date}</td>
                    <td class="td_coin">${coinDisplay}</td>
                `;
                tbody.appendChild(tr);
            });
        }
    };

    // =========================================================
    // ✅ Event Controller 초기화 및 UI 업데이트
    // =========================================================
    const eventHandle = window.IPX_EVENT_HANDLE || '2026-6th-event';
    const controller = new EventController({ eventHandle: eventHandle });
    controller.init();
    const isEnPage = $(".january_event_container.en_ver").length > 0;
    const uiText = isEnPage
        ? {
            mission1Done: "Completed Today",
            mission1Default: "Check In Now",
            mission2Done: (max) => `Order ${max} Times Completed`,
            mission2Default: "Shop Now",
            mission3Done: (max) => `${max} Reviews Completed`,
            mission3Default: "Write a Review",
            bonusAll: { disabled: "Requires Missions 1-3", ready: "Claim Bonus Coins", done: "Claimed" },
            bonusOrder: { disabled: "Requires Mission 2", ready: "Shop Now", done: "Claimed" },
            loginRequired: "Log in to participate",
            processing: "Processing...",
        }
        : {
            mission1Done: "오늘 완료",
            mission1Default: "출석하고 코인 받기",
            mission2Done: (max) => `주문 ${max}회 달성 완료`,
            mission2Default: "바로 주문하러 가기",
            mission3Done: (max) => `리뷰 ${max}회 작성 완료`,
            mission3Default: "리뷰 쓰기",
            bonusAll: { disabled: "미션 1,2,3 완료시 가능", ready: "보너스 코인 받기", done: "획득 완료" },
            bonusOrder: { disabled: "미션 2. 완료시 가능", ready: "바로 주문하러 가기", done: "획득 완료" },
            loginRequired: "로그인 시 참여 가능",
            processing: "처리중...",
        };

    // =========================================================
    // ✅ UI Helper Functions
    // =========================================================
    function updateEtcButtonsDim() {
        const $items = $(".sc_gift .etc_list li[data-state]");
        if (!$items.length) return;

        $items.each(function () {
            const $item = $(this);
            const state = $item.data("state");
            const $btn = $item.find(".btn_wrap button");
            const $icon = $btn.find("img");

            if (state === "done" || state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
            }
        });
    }

    function updateGachaBtnDim() {
        const $wrap = $(".sc_gift .gacha_btn");
        if (!$wrap.length) return;

        $wrap.each(function () {
            const $btnWrap = $(this);
            const state = $btnWrap.attr("data-state");
            const $btn = $btnWrap.find("button");
            const $icon = $btn.find("img");
            const $text = $btn.find("span");

            if (state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
                $text.css("color", "#A0A0A0");
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
                $text.css("color", "");
            }
        });
    }

    function getMissionMaxCount(missionId, fallback) {
        const missions = controller.store.state.config.missions || [];
        const mission = missions.find((item) => String(item.id) === String(missionId));
        return Number((mission && mission.max_count) || fallback || 0);
    }

    function updateCoinDisplay() {
        const currentStamps = controller.store.state.user.currentStamps || 0;
        const freeCoinsToday = controller.store.state.user.freeCoinsToday || 0;
        const totalCoins = currentStamps + freeCoinsToday;

        // 네비게이션 영역 (PC/MO) - 각 코인 타입별로 업데이트
        document.querySelectorAll('.first_coin .coin_count').forEach(el => {
            el.innerText = String(currentStamps);
        });
        document.querySelectorAll('.second_coin .coin_count').forEach(el => {
            el.innerText = String(freeCoinsToday);
        });

        // MY COIN 섹션
        document.querySelectorAll('.coin_number').forEach(el => {
            el.innerText = isEnPage ? `${totalCoins}` : `${totalCoins}개`;
        });

        // 복주머니 열어보기 버튼 활성화/비활성화
        const $luckyButton = $('.lucky_button button');
        if ($luckyButton.length) {
            if (totalCoins <= 0) {
                $luckyButton.prop('disabled', true).attr('data-state', 'disabled').css('background-color', '#a0a0a0');
            } else {
                $luckyButton.prop('disabled', false).removeAttr('data-state').css('background-color', '');
            }
        }

        console.log(`[UI] Coin display updated: first_coin=${currentStamps}, second_coin=${freeCoinsToday}, total=${totalCoins}`);
    }

    function updateMissionBar(missionKey, count, maxCount) {
        const $li = $(`.mission_content li[data-mission="${missionKey}"]`);
        if (!$li.length) return;
        const $cells = $li.find(".mission_bar_track span");
        $cells.removeClass("is-fill");
        $cells.each(function (idx) {
            if (idx < Math.min(count, maxCount)) $(this).addClass("is-fill");
        });
    }

    function updateMissionUI() {
        const counts = controller.store.state.user.missionCounts || {};
        const attendanceHistory = controller.store.state.user.attendanceHistory || [];
        const today = getTodayKST();
        const isTodayChecked = attendanceHistory.includes(today);
        const isLoggedIn = !!controller.store.state.isLoggedIn;

        const attendanceMax = getMissionMaxCount("mission_attendance", 10);
        const orderMax = getMissionMaxCount("mission_order", 3);
        const reviewMax = getMissionMaxCount("mission_review", 5);
        const bonusOrderMax = getMissionMaxCount("mission_bonus_order", 3);
        const signupMax = getMissionMaxCount("mission_signup", 1);

        // =========================================================
        // ✅ 출석 일수 업데이트
        // =========================================================
        const attendanceCount = Number(counts.attendance || 0);
        const $daysSpan = $(".txt_wrap .days");
        if ($daysSpan.length) {
            $daysSpan.text(isEnPage ? `${attendanceCount} days` : `${attendanceCount}일`);
        }

        const mission1Done = isTodayChecked || attendanceCount >= attendanceMax;
        const mission2Done = Number(counts.order || 0) >= orderMax;
        const mission3Done = Number(counts.review || 0) >= reviewMax;

        const missionRules = [
            { key: "mission1_attendance", done: mission1Done, doneText: uiText.mission1Done, defaultText: uiText.mission1Default, max: attendanceMax, count: Number(counts.attendance || 0) },
            { key: "mission2_order", done: mission2Done, doneText: uiText.mission2Done(orderMax), defaultText: uiText.mission2Default, max: orderMax, count: Number(counts.order || 0) },
            { key: "mission3_review", done: mission3Done, doneText: uiText.mission3Done(reviewMax), defaultText: uiText.mission3Default, max: reviewMax, count: Number(counts.review || 0) }
        ];

        missionRules.forEach((rule) => {
            const li = document.querySelector(`.mission_content li[data-mission="${rule.key}"]`);
            if (!li) return;
            li.setAttribute("data-state", rule.done ? "done" : "default");

            const target = li.querySelector(".btn_wrap button, .btn_wrap a");
            if (target) target.textContent = rule.done ? rule.doneText : rule.defaultText;

            updateMissionBar(rule.key, rule.count, rule.max);
        });

        const bonusAllCount = Number(counts.bonusAll || 0);
        const bonusOrderCount = Number(counts.bonusOrder || 0);
        const signupCount = Number(counts.signup || 0);

        const allMissionsDone = mission1Done && mission2Done && mission3Done;
        const bonusAllDone = bonusAllCount >= 1;
        const bonusOrderDone = bonusOrderCount >= bonusOrderMax;

        let bonusAllState = "disabled";
        if (!isLoggedIn) bonusAllState = "disabled";
        else if (bonusAllDone) bonusAllState = "done";
        else if (allMissionsDone) bonusAllState = "ready";
        else bonusAllState = "disabled";

        let bonusOrderState = "disabled";
        if (!isLoggedIn) bonusOrderState = "disabled";
        else if (bonusOrderDone) bonusOrderState = "done";
        else if (mission2Done) bonusOrderState = "ready";
        else bonusOrderState = "disabled";

        const bonusItems = [
            { selector: '.mission_bonus li[data-bonus="mission_bonus_all"]', state: bonusAllState, texts: uiText.bonusAll, max: 1, count: bonusAllCount },
            { selector: '.mission_bonus li[data-bonus="mission_bonus_order"]', state: bonusOrderState, texts: uiText.bonusOrder, max: bonusOrderMax, count: bonusOrderCount }
        ];

        bonusItems.forEach(bonus => {
            const li = document.querySelector(bonus.selector);
            if (!li) return;
            li.setAttribute("data-state", bonus.state);
            const btn = li.querySelector(".btn_wrap button");
            if (!btn) return;

            if (bonus.state === "disabled") btn.textContent = bonus.texts.disabled;
            else if (bonus.state === "done") btn.textContent = bonus.texts.done;
            else btn.textContent = bonus.texts.ready;
        });

        // =========================================================
        // ✅ 출석체크 버튼 텍스트 (260824 주석 요구사항)
        // =========================================================
        const $attendanceBtnWrap = $(".sc_mission .attendance_btn");
        if ($attendanceBtnWrap.length) {
            const attendanceState = $attendanceBtnWrap.attr("data-state");
            const $attendanceBtn = $attendanceBtnWrap.find("button");

            if (!isLoggedIn) {
                $attendanceBtn.text(isEnPage ? "Log In" : "로그인 하기");
            } else if (attendanceState === "done" || isTodayChecked) {
                $attendanceBtnWrap.attr("data-state", "done");
                $attendanceBtn.text(isEnPage ? "See You Tomorrow" : "내일 다시 만나요");
            } else {
                $attendanceBtnWrap.attr("data-state", "ready");
                $attendanceBtn.text(isEnPage ? "Daily Check-in" : "출석체크하기");
            }
        }

        // =========================================================
        // ✅ 보너스 1 (방문 혜택) 버튼 텍스트 (260824 주석 요구사항)
        // =========================================================
        const $bonusVisitBtnWrap = $(".sc_mission .bonus_visit_btn");
        if ($bonusVisitBtnWrap.length) {
            const dailyFreeCoinHistory = controller.store.state.user.dailyFreeCoinHistory || [];
            const alreadyClaimed = dailyFreeCoinHistory.includes(today);
            const $bonusVisitBtn = $bonusVisitBtnWrap.find("button");

            if (!isLoggedIn) {
                $bonusVisitBtn.text(isEnPage ? "Log In" : "로그인 하기");
            } else if (alreadyClaimed) {
                $bonusVisitBtnWrap.attr("data-state", "done");
                $bonusVisitBtn.text(isEnPage ? "See You Tomorrow" : "내일 다시 만나요");
            } else {
                $bonusVisitBtnWrap.attr("data-state", "ready");
                $bonusVisitBtn.text(isEnPage ? "Claim Golden Lucky Bag" : "황금 복주머니 받기");
            }
        }

        // =========================================================
        // ✅ 보너스 2 (추천 상품 보기) 버튼 텍스트 (260824 주석 요구사항)
        // =========================================================
        const $bonusProductBtnWrap = $(".sc_mission .bonus_product_btn");
        if ($bonusProductBtnWrap.length) {
            const viewRecommendedProductsHistory = controller.store.state.user.viewRecommendedProductsHistory || [];
            const alreadyViewedToday = viewRecommendedProductsHistory.includes(today);
            const $bonusProductBtn = $bonusProductBtnWrap.find("button");

            if (!isLoggedIn) {
                $bonusProductBtn.text(isEnPage ? "Log In" : "로그인 하기");
            } else if (alreadyViewedToday) {
                $bonusProductBtnWrap.attr("data-state", "done");
                $bonusProductBtn.text(isEnPage ? "See You Tomorrow" : "내일 다시 만나요");
            } else {
                $bonusProductBtnWrap.attr("data-state", "ready");
                $bonusProductBtn.text(isEnPage ? "View Recommended Items" : "추천 상품 보기");
            }
        }

        // =========================================================
        // ✅ 보너스 3 (추석 이벤트) 버튼 텍스트 (260824 주석 요구사항)
        // =========================================================
        const $bonusChuseokBtnWrap = $(".sc_mission .bonus_chuseok_btn");
        if ($bonusChuseokBtnWrap.length) {
            const specialFreeBonusHistory = controller.store.state.user.specialFreeBonusHistory || [];
            const alreadyClaimedToday = specialFreeBonusHistory.includes(today);
            const $chuseokBtn = $bonusChuseokBtnWrap.find("button");

            // 한국 시간(KST) 기준으로 현재 날짜 가져오기
            const todayKST = getTodayKST(); // "YYYY-MM-DD" 형식
            const eventStartDate = "2026-09-24";
            const eventEndDate = "2026-09-26"; // 9월 26일까지 포함

            if (todayKST < eventStartDate) {
                // 이벤트 시작 전 (9월 24일 이전)
                $chuseokBtn.text("9.24 ~ 9.26");
                $bonusChuseokBtnWrap.attr("data-state", "disabled");
            } else if (todayKST > eventEndDate) {
                // 이벤트 종료 (9월 27일 이후)
                $chuseokBtn.text(isEnPage ? "Chuseok Event Ended" : "추석 이벤트 종료");
                $bonusChuseokBtnWrap.attr("data-state", "disabled");
            } else if (!isLoggedIn) {
                // 이벤트 기간 중이지만 로그인 필요
                $chuseokBtn.text(isEnPage ? "Log In" : "로그인 하기");
                $bonusChuseokBtnWrap.attr("data-state", "ready");
            } else if (alreadyClaimedToday) {
                // 이벤트 진행 중 + 오늘 이미 수령 완료
                $chuseokBtn.text(isEnPage ? "See You Tomorrow" : "내일 다시 만나요");
                $bonusChuseokBtnWrap.attr("data-state", "done");
            } else {
                // 이벤트 진행 중 + 수령 가능
                $bonusChuseokBtnWrap.attr("data-state", "ready");
                $chuseokBtn.text(isEnPage ? "Claim Chuseok Lucky Bag" : "추석 복주머니 받기");
            }
        }

        // Claim free coins button (기존 로직 - 보너스1과 중복될 수 있으나 유지)
        const claimFreeCoinsBtn = document.querySelector('[data-action="claim_free_coins"]');
        if (claimFreeCoinsBtn) {
            const freeCoinDates = controller.store.state.user.freeCoinDates || [];
            const alreadyClaimed = freeCoinDates.includes(today);

            const li = claimFreeCoinsBtn.closest('li[data-state]');
            if (li) {
                if (alreadyClaimed) {
                    li.setAttribute('data-state', 'done');
                    claimFreeCoinsBtn.innerText = isEnPage ? 'Claimed' : '획득 완료';
                    claimFreeCoinsBtn.disabled = true;
                } else {
                    li.setAttribute('data-state', 'ready');
                    claimFreeCoinsBtn.innerText = isEnPage ? 'Claim Bonus Coins' : '보너스 코인 받기';
                    claimFreeCoinsBtn.disabled = false;
                }
            } else {
                if (!isLoggedIn) {
                    claimFreeCoinsBtn.textContent = uiText.loginRequired;
                }
            }
        }

        // Attendance calendar count display
        const $countEl = $(".sc_mission .attendance_btn .count");
        const count = Number(counts.attendance || 0);
        if ($countEl.length) isEnPage ? $countEl.text(`${count}days`) : $countEl.text(`${count}일`);
    }

    function applyAttendanceHistoryToCalendar() {
        const attendanceHistory = controller.store.state.user.attendanceHistory || [];
        const $dayItems = $(".attendance_days .day_item");
        const today = getTodayKST();

        console.log('[Calendar] Starting to apply attendance history');
        console.log('[Calendar] Attendance history:', attendanceHistory);
        console.log('[Calendar] Today (KST):', today);
        console.log('[Calendar] Found day items:', $dayItems.length);

        $dayItems.each(function () {
            const $item = $(this);
            const dateStr = $item.attr("data-date");

            // data-date가 없는 항목은 건너뛰기 (빈 칸)
            if (!dateStr) return;

            // 출석 완료한 날짜인 경우
            if (attendanceHistory.includes(dateStr)) {
                console.log('[Calendar] Setting done state for:', dateStr);
                $item.attr("data-state", "done");
                // 강제로 day_img 표시
                $item.find(".day_img").css("display", "block");
            } else {
                // 오늘 날짜인 경우
                if (dateStr === today) {
                    $item.attr("data-state", "ready");
                }
                // 과거 날짜인 경우 (미출석)
                else if (dateStr < today) {
                    $item.attr("data-state", "locked");
                }
                // 미래 날짜인 경우
                else {
                    $item.attr("data-state", "locked");
                }
            }
        });

        // 출석체크 버튼 상태 업데이트
        const confirmBtn = document.querySelector('[data-action="confirm_attendance"]');
        if (confirmBtn) {
            const isTodayChecked = attendanceHistory.includes(today);
            if (isTodayChecked) {
                confirmBtn.innerText = isEnPage ? 'Checked In Today' : '오늘 출석 완료';
                confirmBtn.disabled = true;
                confirmBtn.setAttribute('data-state', 'done_today');
            } else {
                confirmBtn.innerText = isEnPage ? 'Check In' : '출석체크 하기';
                confirmBtn.disabled = false;
                confirmBtn.setAttribute('data-state', 'default');
            }
        }

        console.log('[Calendar] Finished applying attendance history');
    }

    function syncUserStateFromPayload(payload) {
        if (!payload) {
            console.warn('[syncUserStateFromPayload] No payload provided');
            return;
        }

        try {
            if (payload.current_stamps !== undefined) {
                controller.store.state.user.currentStamps = Number(payload.current_stamps) || 0;
            }
            if (payload.free_coins_today !== undefined) {
                controller.store.state.user.freeCoinsToday = Number(payload.free_coins_today) || 0;
            }
            if (payload.rewards && Array.isArray(payload.rewards.claimed_ids)) {
                controller.store.state.user.claimedRewards = payload.rewards.claimed_ids;
            }
            if (payload.free_coin_dates && Array.isArray(payload.free_coin_dates)) {
                controller.store.state.user.freeCoinDates = payload.free_coin_dates;
            }

            if (payload.mission_progress) {
                const mp = payload.mission_progress;
                const attendance = mp.mission_attendance || mp.attendance || {};
                const dailyFreeCoins = mp.daily_free_coins || {};
                const specialFreeBonus = mp.special_free_bonus || {};
                const viewRecommendedProducts = mp.view_recommeded_products || {};
                const order = mp.mission_order || {};
                const review = mp.mission_review || {};
                const bonusAll = mp.all_missions_complete_bonus || mp.mission_bonus_all || {};
                const bonusOrder = mp.mission_bonus_order || {};

                if (attendance.history && Array.isArray(attendance.history)) {
                    controller.store.state.user.attendanceHistory = attendance.history;
                }
                if (dailyFreeCoins.history && Array.isArray(dailyFreeCoins.history)) {
                    controller.store.state.user.dailyFreeCoinHistory = dailyFreeCoins.history;
                }
                if (specialFreeBonus.history && Array.isArray(specialFreeBonus.history)) {
                    controller.store.state.user.specialFreeBonusHistory = specialFreeBonus.history;
                }
                if (viewRecommendedProducts.list && Array.isArray(viewRecommendedProducts.list)) {
                    controller.store.state.user.viewRecommendedProductsHistory = viewRecommendedProducts.list;
                }
                controller.store.state.user.missionCounts.attendance = Number(attendance.count || 0);
                controller.store.state.user.missionCounts.order = Number(order.count || 0);
                controller.store.state.user.missionCounts.review = Number(review.count || 0);
                controller.store.state.user.missionCounts.bonusAll = Number(bonusAll.count || 0);
                controller.store.state.user.missionCounts.bonusOrder = Number(bonusOrder.count || 0);
            }

            const claimedList = controller.store.state.user.claimedRewards || [];
            $('.sc_gift .etc_list li').each(function() {
                const $li = $(this);
                const $btn = $li.find('.btn_exchange');
                const rewardId = $btn.attr('data-reward-id');
                if (rewardId && claimedList.includes(String(rewardId))) {
                    $li.attr('data-state', 'done');
                }
            });
        } catch (err) {
            console.error('[syncUserStateFromPayload] Error:', err);
        }
    }

    function updateButtonsUI() {
        const isLoggedIn = controller.store.state.isLoggedIn;
        const claimedRewards = controller.store.state.user.claimedRewards || [];

        $('.sc_gift .etc_list li').each(function() {
            const $li = $(this);
            const $btn = $li.find('.btn_exchange');
            const rewardId = $btn.attr('data-reward-id');
            if (!rewardId) return;

            if (!isLoggedIn) {
                $li.attr('data-state', 'disabled');
            } else if (claimedRewards.includes(String(rewardId))) {
                $li.attr('data-state', 'done');
            } else {
                const currentState = $li.attr('data-state');
                if (currentState !== 'soldout') {
                    $li.attr('data-state', 'default');
                }
            }
        });

        updateEtcButtonsDim();
        updateGachaBtnDim();
    }

    const fetchLatestStatus = async () => {
        if (!controller.api) {
            updateCoinDisplay();
            updateButtonsUI();
            applyAttendanceHistoryToCalendar(); // 비로그인 상태에서도 캘린더 초기화
            return;
        }

        try {
            const response = await controller.api.getHistory(1);
            syncUserStateFromPayload(response);
            updateCoinDisplay();
            updateMissionUI();
            applyAttendanceHistoryToCalendar();
            updateButtonsUI();
        } catch (error) {
            console.error("[API] 스탬프 동기화 실패:", error);
        }
    };

    // Make controller and fetchLatestStatus globally accessible
    window.eventController = controller;
    window.fetchLatestStatus = fetchLatestStatus;

    // =========================================================
    // sc_bday 이벤트 카드 / 오픈 버튼 떠오르는 모션
    // =========================================================
    (function initBdayItemMotion() {
        var bdayItems = document.querySelectorAll(
            ".sc_bday .event_wrap, .sc_bday .open_button",
        );

        if (!bdayItems.length) return;

        bdayItems.forEach(function (item) {
            gsap.set(item, {
                opacity: 0,
                y: 50,
            });

            gsap.to(item, {
                opacity: 1,
                y: 0,
                duration: 0.7,
                ease: "power2.out",

                scrollTrigger: {
                    trigger: item,

                    /*
                     * 요소 상단이 화면 아래쪽 85% 지점에 닿으면 실행
                     */
                    start: "top 85%",

                    /*
                     * 한 번만 실행
                     */
                    once: true,

                    invalidateOnRefresh: true,

                    // markers: true,
                },
            });
        });
    })();
    // ✅ gsap/ScrollTrigger 로드 확인 + 등록을 맨 위로
    if (!window.gsap || !window.ScrollTrigger) {
        console.warn("GSAP 또는 ScrollTrigger가 로드되지 않았습니다.");
        return;
    }

    // =========================================================
    // Sticky: sc_gift부터 시작, sc_bday까지 유지
    // =========================================================
    // =========================================================
    // Sticky: sc_gift부터 시작, sc_bday가 완전히 끝날 때까지 유지
    // =========================================================
    // =========================================================
    // Sticky: sc_gift부터 시작, sc_mission까지 유지
    // =========================================================
    // =========================================================
    // Sticky: sc_gift부터 sc_september 끝까지
    // =========================================================
    if (
        $(".sticky_wrap").length &&
        $(".sc_gift").length &&
        $(".sc_mission").length &&
        $(".sc_september").length
    ) {
        ScrollTrigger.create({
            trigger: ".sc_gift",
            start: "top top",

            endTrigger: ".sc_september",
            end: "bottom top",

            toggleClass: {
                targets: ".sticky_wrap",
                className: "type_fixed",
            },

            invalidateOnRefresh: true,
            // markers: true,
        });
    }
    // =========================
    // ✅ 모바일 토글
    // =========================
    const $moLineWrap = $(".line_wrap.mo");
    const $moBtn = $moLineWrap.find(".line_btn");
    const $moPanel = $moLineWrap.find(".toggle_panel");

    if ($moLineWrap.length && $moBtn.length && $moPanel.length) {
        $moPanel.css("height", 0);
        let isOpen = false;

        function openMoPanel() {
            const h = $moPanel.prop("scrollHeight");

            $moBtn.attr("aria-expanded", "true");
            $moPanel.attr("aria-hidden", "false");

            // ✅ 열림 상태 클래스
            $moLineWrap.addClass("is-open");

            gsap.to($moPanel, {
                height: h,
                duration: 0.2,
                ease: "power1.out",
                onComplete: () => $moPanel.css("height", "auto"),
            });

            isOpen = true;
        }

        function closeMoPanel() {
            const currentH = $moPanel.outerHeight();
            $moPanel.css("height", currentH);

            $moBtn.attr("aria-expanded", "false");
            $moPanel.attr("aria-hidden", "true");

            // ✅ 열림 상태 클래스 제거
            $moLineWrap.removeClass("is-open");

            gsap.to($moPanel, {
                height: 0,
                duration: 0.2,
                ease: "power1.in",
            });

            isOpen = false;
        }

        $moBtn.on("click", function () {
            isOpen ? closeMoPanel() : openMoPanel();
        });

        $moPanel.find("a").on("click", function () {
            if (isOpen) closeMoPanel();
        });
    } else {
        // 모바일 요소가 없는 페이지/브레이크포인트일 때도 전체 코드가 죽지 않게
        // console.log("MO sticky UI 없음: 토글 스킵");
    }

    // =========================================================
    // ✅ 비로그인 상태 버튼 텍스트 변경 (sc_mission)
    // =========================================================
    // if ($(".january_event_container[data-auth='out']").length) {
    //   $(".sc_mission .btn_wrap button").text("로그인 하기");
    // }

    // =========================================================
    // ✅ 비로그인 상태 버튼 비활성화 처리 (sc_gift)
    // =========================================================
    const $container = $(".january_event_container.ko_ver");

    if ($container.length && $container.data("auth") === "out") {
        $(".sc_gift .gacha_btn").attr("data-state", "disabled");
        $(".sc_gift .etc_list li[data-state]").attr("data-state", "disabled");
        $(".sc_gift .gacha_btn button, .sc_gift .etc_list .btn_wrap button").prop(
            "disabled",
            true,
        );
    }

    // =========================================================
    // ✅ sc_coin 팝업 (코인 교환 이력)
    // =========================================================
    (function initCoinPopup() {
        const $coinPopup = $(".coin_popup");
        if (!$coinPopup.length) return;

        const $coinOpenBtn = $(".coin_popup_open");
        const $coinCloseBtn = $coinPopup.find(".popup_close");

        const $popupContent = $coinPopup.find(".popup_content"); // 드래그 유지 범위
        const $scrollArea = $coinPopup.find(".history_scroll");
        const $rail = $coinPopup.find(".scroll_rail");
        const $thumb = $coinPopup.find(".scroll_thumb");

        function toggleHistoryEmpty() {
            const $tbody = $coinPopup.find("tbody");
            const hasHistory = $tbody.find(".history_row").length > 0;
            $tbody.find(".history_empty").toggle(!hasHistory);
        }

        let savedScrollY = 0;

        function lockBodyScroll() {
            savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;

            $("body").addClass("is-popup-open").css({
                position: "fixed",
                top: -savedScrollY,
                left: 0,
                right: 0,
                width: "100%",
            });
        }

        function unlockBodyScroll() {
            const top = $("body").css("top");

            $("body").removeClass("is-popup-open").css({
                position: "",
                top: "",
                left: "",
                right: "",
                width: "",
            });

            const y = top ? Math.abs(parseInt(top, 10)) : savedScrollY;
            window.scrollTo(0, y);
        }

        function openCoinPopup() {
            toggleHistoryEmpty();
            $coinPopup.show();
            lockBodyScroll();
            updateThumb();
        }

        function closeCoinPopup() {
            stopDrag();
            $coinPopup.hide();
            unlockBodyScroll();
        }

        // 스크롤바 구성요소 없으면 드래그만 스킵하고, 팝업은 열리게 둠
        if (
            !$scrollArea.length ||
            !$rail.length ||
            !$thumb.length ||
            !$popupContent.length
        ) {
            $coinOpenBtn.on("click", function (e) {
                e.preventDefault();
                openCoinPopup();
            });
            $coinCloseBtn.on("click", function (e) {
                e.preventDefault();
                closeCoinPopup();
            });
            $coinPopup.on("click", ".dimmed_wrap", function (e) {
                e.preventDefault();
                closeCoinPopup();
            });
            $(document).on("keydown", function (e) {
                if (e.key === "Escape" && $coinPopup.is(":visible")) closeCoinPopup();
            });
            return;
        }

        const MIN_THUMB = 36;
        let isDragging = false;
        let dragOffsetY = 0;

        function clamp(v, min, max) {
            return Math.max(min, Math.min(max, v));
        }

        function setScrollTopByThumbTop(thumbTop) {
            const el = $scrollArea[0];
            const viewH = el.clientHeight;
            const contentH = el.scrollHeight;

            const railH = $rail[0].clientHeight;
            const thumbH = $thumb[0].clientHeight;

            const maxThumbTop = railH - thumbH;
            const ratio = maxThumbTop ? thumbTop / maxThumbTop : 0;

            el.scrollTop = ratio * (contentH - viewH);
        }

        function updateThumb() {
            const el = $scrollArea[0];
            const viewH = el.clientHeight;
            const contentH = el.scrollHeight;

            if (contentH <= viewH + 1) {
                $rail.hide();
                return;
            }
            $rail.show();

            const railH = $rail[0].clientHeight;
            const maxScroll = contentH - viewH;

            const thumbH = Math.max(MIN_THUMB, (viewH / contentH) * railH);
            $thumb.height(thumbH);

            const maxThumbTop = railH - thumbH;
            const ratio = maxScroll ? el.scrollTop / maxScroll : 0;
            $thumb.css("top", maxThumbTop * ratio);
        }

        $scrollArea.on("scroll", function () {
            if (!isDragging) updateThumb();
        });

        function isPointInsideContent(clientX, clientY) {
            const rect = $popupContent[0].getBoundingClientRect();
            return (
                clientX >= rect.left &&
                clientX <= rect.right &&
                clientY >= rect.top &&
                clientY <= rect.bottom
            );
        }

        function stopDrag() {
            isDragging = false;
            $(document).off(".coinThumb");
        }

        $thumb.on("mousedown", function (e) {
            e.preventDefault();
            isDragging = true;

            const thumbRect = $thumb[0].getBoundingClientRect();
            dragOffsetY = e.clientY - thumbRect.top;

            $(document).on("mousemove.coinThumb", function (ev) {
                if (!isPointInsideContent(ev.clientX, ev.clientY)) {
                    stopDrag();
                    return;
                }

                const railRect = $rail[0].getBoundingClientRect();
                const thumbH = $thumb[0].clientHeight;

                let top = ev.clientY - railRect.top - dragOffsetY;
                top = clamp(top, 0, railRect.height - thumbH);

                $thumb.css("top", top);
                setScrollTopByThumbTop(top);
            });

            $(document).on("mouseup.coinThumb", function () {
                stopDrag();
            });
        });

        $rail.on("mousedown", function (e) {
            if ($(e.target).closest(".scroll_thumb").length) return;

            const railRect = $rail[0].getBoundingClientRect();
            const thumbH = $thumb[0].clientHeight;

            let top = e.clientY - railRect.top - thumbH / 2;
            top = clamp(top, 0, railRect.height - thumbH);

            $thumb.css("top", top);
            setScrollTopByThumbTop(top);
        });

        $(window).on("resize", updateThumb);

        // 열기/닫기 바인딩
        $coinOpenBtn.on("click", async function (e) {
            e.preventDefault();

            if (!controller.api) {
                alert('로그인이 필요합니다.');
                return;
            }

            try {
                const res = await controller.api.getHistory(50);
                renderHistory(res.history || []);
                openCoinPopup();
            } catch (err) {
                console.error('[coinPopup] 히스토리 로드 실패:', err);
                alert('히스토리를 불러오는 중 오류가 발생했습니다.');
            }
        });

        $coinCloseBtn.on("click", function (e) {
            e.preventDefault();
            closeCoinPopup();
        });

        $coinPopup.on("click", ".dimmed_wrap", function (e) {
            e.preventDefault();
            closeCoinPopup();
        });

        $(document).on("keydown", function (e) {
            if (e.key === "Escape" && $coinPopup.is(":visible")) closeCoinPopup();
        });

        // 초기
        toggleHistoryEmpty();
        updateThumb();
    })();

    // =========================================================
    // ✅ sc_gift: etc_list 버튼 dim 처리 (disabled/done/soldout)
    // =========================================================
    (function initEtcButtonsDim() {
        const $items = $(".sc_gift .etc_list li[data-state]");
        if (!$items.length) return;

        $items.each(function () {
            const $item = $(this);
            const state = $item.data("state");

            const $btn = $item.find(".btn_wrap button");
            const $icon = $btn.find("img");

            if (state === "done" || state === "soldout" || state === "disabled") {
                $icon.attr(
                    "src",
                    "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png",
                );
                $btn.prop("disabled", true);
            } else {
                $icon.attr(
                    "src",
                    "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png",
                );
                $btn.prop("disabled", false);
            }
        });
    })();

    // =========================================================
    // ✅ sc_gift: gacha_btn 처리 (disabled/default/soldout)
    // =========================================================
    (function initGachaBtnDim() {
        const $wrap = $(".sc_gift .gacha_btn[data-state]");
        if (!$wrap.length) return;

        $wrap.each(function () {
            const $btnWrap = $(this);
            const state = $btnWrap.data("state");

            const $btn = $btnWrap.find("button");
            const $icon = $btn.find("img");
            const $text = $btn.find("span");

            if (state === "soldout" || state === "disabled") {
                $icon.attr(
                    "src",
                    "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png",
                );
                $btn.prop("disabled", true);
                $text.css("color", "#A0A0A0");
            } else {
                $icon.attr(
                    "src",
                    "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png",
                );
                $btn.prop("disabled", false);
                $text.css("color", "");
            }
        });
    })();

    // =========================================================
    // ✅ 캘린더(출석) 팝업: 열기/닫기
    // =========================================================
    (function initAttendancePopup() {
        const $attendancePopup = $(
            '.attendance_popup[data-popup="attendance_popup"]',
        );
        if (!$attendancePopup.length) return;

        const $openBtn = $(".sc_mission .attendance_btn button");
        const $closeBtn = $attendancePopup.find(".popup_close");

        function openAttendancePopup() {
            // 팝업을 열기 전에 최신 출석 상태를 캘린더에 반영
            applyAttendanceHistoryToCalendar();
            $attendancePopup.show();
            $("body").addClass("is-popup-open");
        }

        function closeAttendancePopup() {
            $attendancePopup.hide();
            $("body").removeClass("is-popup-open");
        }

        // 열기
        $openBtn.on("click", function (e) {
            e.preventDefault();
            // data-redirect 속성이 있으면 팝업을 열지 않음 (비로그인 상태)
            if ($(this).attr('data-redirect')) {
                return;
            }
            openAttendancePopup();
        });

        // 닫기 버튼
        $closeBtn.on("click", function (e) {
            e.preventDefault();
            closeAttendancePopup();
        });

        // ✅ mo dimmed 클릭 시 닫기
        $attendancePopup.on("click", ".dimmed_wrap.mo_only", function (e) {
            e.preventDefault();
            closeAttendancePopup();
        });
    })();

    // =========================================================
    // ✅ 출석 체크 버튼
    // =========================================================
    const confirmAttendanceBtn = document.querySelector('[data-action="confirm_attendance"]');
    if (confirmAttendanceBtn) {
        confirmAttendanceBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            if (confirmAttendanceBtn.disabled) return;

            const originalText = confirmAttendanceBtn.innerText;
            const isEnPage = document.querySelector(".january_event_container.en_ver") !== null;

            confirmAttendanceBtn.disabled = true;
            confirmAttendanceBtn.innerText = isEnPage ? "Processing..." : "처리중...";

            try {
                const controller = window.eventController;
                const isSuccess = await controller.tryCheckIn();

                if (isSuccess) {
                    if (typeof window.fetchLatestStatus === 'function') {
                        await window.fetchLatestStatus();
                    }

                    confirmAttendanceBtn.setAttribute('data-state', 'done_today');
                    confirmAttendanceBtn.innerText = isEnPage ? 'Checked In Today' : '오늘 출석 완료';
                    confirmAttendanceBtn.disabled = true;
                } else {
                    confirmAttendanceBtn.disabled = false;
                    confirmAttendanceBtn.innerText = originalText;
                }
            } catch (err) {
                console.error('[confirmAttendance] Error:', err);
                confirmAttendanceBtn.disabled = false;
                confirmAttendanceBtn.innerText = originalText;
            }
        });
    }

    // =========================================================
    // ✅ 보너스 코인 받기 버튼
    // =========================================================
    const claimFreeCoinsBtn = document.querySelector('[data-action="claim_free_coins"]');
    if (claimFreeCoinsBtn) {
        claimFreeCoinsBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            const li = claimFreeCoinsBtn.closest('li[data-state]');
            const currentState = li ? li.getAttribute('data-state') : 'ready';

            if (currentState === 'done' || claimFreeCoinsBtn.disabled) return;

            const originalText = claimFreeCoinsBtn.innerText;

            claimFreeCoinsBtn.disabled = true;
            claimFreeCoinsBtn.innerText = uiText.processing;

            try {
                const result = await controller.tryClaimFreeCoins();

                if (result.success) {

                    // 전체 상태 동기화
                    await fetchLatestStatus();

                    if (li) li.setAttribute('data-state', 'done');
                    claimFreeCoinsBtn.innerText = isEnPage ? 'Claimed' : '획득 완료';
                    claimFreeCoinsBtn.disabled = true;

                } else {
                    claimFreeCoinsBtn.disabled = false;
                    claimFreeCoinsBtn.innerText = originalText;
                }
            } catch (err) {
                console.error('[claimFreeCoins] Error:', err);
                claimFreeCoinsBtn.disabled = false;
                claimFreeCoinsBtn.innerText = originalText;
            }
        });
    }

    // =========================================================
    // ✅ 보너스 1 (방문 혜택 - 황금 복주머니 받기) 버튼
    // =========================================================
    const bonusVisitBtn = document.querySelector('.bonus_visit_btn button');
    if (bonusVisitBtn) {
        bonusVisitBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            // data-redirect 속성이 있으면 리다이렉트 (비로그인 상태)
            if (bonusVisitBtn.getAttribute('data-redirect')) {
                const redirectUrl = bonusVisitBtn.getAttribute('data-redirect');
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                return;
            }

            const $btnWrap = $(bonusVisitBtn).closest('.bonus_visit_btn');
            const currentState = $btnWrap.attr('data-state');

            if (currentState === 'done' || bonusVisitBtn.disabled) {
                return;
            }

            const originalText = bonusVisitBtn.innerText;

            bonusVisitBtn.disabled = true;
            bonusVisitBtn.innerText = uiText.processing;

            try {
                // daily_free_coins missionId로 출석체크 API 호출
                const isSuccess = await controller.tryCheckIn('daily_free_coins');

                if (isSuccess) {
                    // 전체 상태 동기화
                    await fetchLatestStatus();

                    $btnWrap.attr('data-state', 'done');
                    bonusVisitBtn.innerText = isEnPage ? 'See You Tomorrow' : '내일 다시 만나요';
                    bonusVisitBtn.disabled = true;
                } else {
                    bonusVisitBtn.disabled = false;
                    bonusVisitBtn.innerText = originalText;
                }
            } catch (err) {
                console.error('[bonusVisit] Error:', err);
                bonusVisitBtn.disabled = false;
                bonusVisitBtn.innerText = originalText;
            }
        });
    }

    // =========================================================
    // ✅ 보너스 2 (추천 상품 보기) 버튼
    // =========================================================
    const bonusProductBtn = document.querySelector('.bonus_product_btn button');
    console.log('[bonusProduct] Button element found:', bonusProductBtn);
    if (bonusProductBtn) {
        bonusProductBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            // data-redirect 속성이 있으면 리다이렉트 (비로그인 상태)
            if (bonusProductBtn.getAttribute('data-redirect')) {
                const redirectUrl = bonusProductBtn.getAttribute('data-redirect');
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                return;
            }

            const $btnWrap = $(bonusProductBtn).closest('.bonus_product_btn');
            const currentState = $btnWrap.attr('data-state');

            if (currentState === 'done' || bonusProductBtn.disabled) {
                return;
            }

            const originalText = bonusProductBtn.innerText;
            const isEnPage = document.querySelector(".january_event_container.en_ver") !== null;

            console.log('[bonusProduct] Button clicked, current text:', originalText);

            bonusProductBtn.disabled = true;
            bonusProductBtn.innerText = isEnPage ? "Processing..." : "처리중...";

            try {
                // 1. webhook API 호출 (view_recommeded_products)
                const controller = window.eventController;
                console.log('[bonusProduct] Controller:', controller);

                if (!controller) {
                    throw new Error('Controller not found');
                }

                console.log('[bonusProduct] Calling webhook...');
                const webhookSuccess = await controller.triggerWebhook('view_recommeded_products');
                console.log('[bonusProduct] Webhook result:', webhookSuccess);

                if (webhookSuccess) {
                    // 2. 최신 상태 가져오기 및 UI 업데이트
                    console.log('[bonusProduct] Fetching latest status...');
                    if (typeof window.fetchLatestStatus === 'function') {
                        await window.fetchLatestStatus();
                        console.log('[bonusProduct] Status fetched successfully');
                    } else {
                        console.error('[bonusProduct] fetchLatestStatus not available');
                    }

                    // 3. 추천 상품 페이지를 새 창으로 열기
                    const shopUrl = isEnPage
                        ? 'https://linefriendssquare.com/en/collections/featured-lucky-bag-challenge'
                        : 'https://linefriendssquare.com/collections/featured-lucky-bag-challenge';
                    console.log('[bonusProduct] Opening shop URL:', shopUrl);
                    const newWindow = window.open(shopUrl, '_blank');
                    console.log('[bonusProduct] Window opened:', newWindow);
                } else {
                    console.log('[bonusProduct] Webhook failed, restoring button');
                    bonusProductBtn.disabled = false;
                    bonusProductBtn.innerText = originalText;
                }
            } catch (err) {
                console.error('[bonusProduct] Error:', err);
                alert('오류가 발생했습니다: ' + err.message);
                bonusProductBtn.disabled = false;
                bonusProductBtn.innerText = originalText;
            }
        });
    }

    // =========================================================
    // ✅ 보너스 3 (추석 황금 복주머니) 버튼
    // =========================================================
    const bonusChuseokBtn = document.querySelector('.bonus_chuseok_btn button');
    if (bonusChuseokBtn) {
        bonusChuseokBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            // data-redirect 속성이 있으면 리다이렉트 (비로그인 상태)
            if (bonusChuseokBtn.getAttribute('data-redirect')) {
                const redirectUrl = bonusChuseokBtn.getAttribute('data-redirect');
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                return;
            }

            const $btnWrap = $(bonusChuseokBtn).closest('.bonus_chuseok_btn');
            const currentState = $btnWrap.attr('data-state');

            if (currentState === 'done' || currentState === 'disabled' || bonusChuseokBtn.disabled) {
                return;
            }

            const originalText = bonusChuseokBtn.innerText;

            bonusChuseokBtn.disabled = true;
            bonusChuseokBtn.innerText = uiText.processing;

            try {
                // special_free_bonus missionId로 출석체크 API 호출
                const isSuccess = await controller.tryCheckIn('special_free_bonus');

                if (isSuccess) {
                    // 전체 상태 동기화 (event/history 호출하여 복주머니 카운트 업데이트)
                    await fetchLatestStatus();

                    $btnWrap.attr('data-state', 'done');
                    bonusChuseokBtn.innerText = isEnPage ? 'See You Tomorrow' : '내일 다시 만나요';
                    bonusChuseokBtn.disabled = true;
                } else {
                    bonusChuseokBtn.disabled = false;
                    bonusChuseokBtn.innerText = originalText;
                }
            } catch (err) {
                console.error('[bonusChuseok] Error:', err);
                bonusChuseokBtn.disabled = false;
                bonusChuseokBtn.innerText = originalText;
            }
        });
    }

    // =========================================================
    // ✅ 캘린더(출석) 팝업: 990 이하에서는 이미지 경로 모바일로
    // =========================================================
    const BREAKPOINT = 990;

    function updateAttendanceImages() {
        const isMobile = window.innerWidth <= BREAKPOINT;

        $(".attendance_days .day_item img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");

            if (!src) return;

            // pc → mo
            if (isMobile && src.includes("/pc/")) {
                $img.attr("src", src.replace("/pc/", "/mo/"));
            }

            // mo → pc
            if (!isMobile && src.includes("/mo/")) {
                $img.attr("src", src.replace("/mo/", "/pc/"));
            }
        });
    }

    updateAttendanceImages();

    $(window).on("resize", function () {
        updateAttendanceImages();
    });

    // =========================================================
    // ✅ 가챠: GIF 6초 재생 → (gacha_popup 오픈 + lottie 재생) // 딜레이22
    // =========================================================
    // =========================================================
    // 가챠 결과 팝업 + 파티클
    // =========================================================
    (function initGachaFlow() {
        const $gachaPopup = $('.gacha_popup[data-popup="gacha_result"]');
        const $motionWrap = $(".sc_gift .motion_wrap");

        // 팝업이 없을 때만 종료
        if (!$gachaPopup.length) return;

        // 가챠 결과 UI 컨텐츠 가져오기
        const getGachaResultUiContent = (resultId, descText = "", typeText = "") => {
            const copyByResultId = isEnPage
                ? {
                    item_1st_gacha: {
                        resultRank: "1st",
                        badgeText: "1st Place",
                        imgFileName: "gacha_result_img_01.png",
                        titleHtml: `<span class="bold_span">RECTO X BROWN <br class="mo_only">Classic Plush <br class="pc_only">(Vintage / <br class="mo_only">Chocolate Brown) Set</span>`,
                        productUrl: "https://linefriendssquare.com/products/recto-x-brown-classic-plush-set"
                    },
                    item_2st_gacha: {
                        resultRank: "2nd",
                        badgeText: "2nd Place",
                        imgFileName: "gacha_result_img_02.png",
                        titleHtml: `<span class="bold_span">LOFA Seoul X BROWN <br>Wine Glass Set</span>`,
                        productUrl: "https://linefriendssquare.com/products/lofa-seoul-x-brown-wine-glass-set"
                    },
                    item_3st_gacha: {
                        resultRank: "3rd",
                        badgeText: "3rd Place",
                        imgFileName: "gacha_result_img_03.png",
                        titleHtml: `<span class="bold_span">LINEFRIENDS Magsafe <br class="mo_only"> 3 in 1 <br class="pc_only">Wireless <br class="mo_only">Charging Stand</span>`,
                        productUrl: "https://linefriendssquare.com/products/line-friends-magsafe-3in1-wireless-car-charger"
                    },
                    item_4st_gacha: {
                        resultRank: "4th",
                        badgeText: "4th Place",
                        imgFileName: "gacha_result_img_04.png",
                        titleHtml: `<span class="bold_span">₩20,000 Cart Coupon <br>(Orders over ₩50,000)</span>`,
                        productUrl: ""
                    },
                    item_5st_gacha: {
                        resultRank: "5th",
                        badgeText: "5th Place",
                        imgFileName: "gacha_result_img_05.png",
                        titleHtml: `<span class="bold_span">₩5,000 Cart Coupon <br>(Orders over ₩20,000)</span>`,
                        productUrl: ""
                    },
                    item_6st_gacha: {
                        resultRank: "lose",
                        badgeText: "No Prize",
                        imgFileName: "gacha_result_img_06.png",
                        titleHtml: `Better luck next time...`,
                        productUrl: ""
                    },
                }
                : {
                    item_1st_gacha: {
                        resultRank: "1st",
                        badgeText: "1등",
                        imgFileName: "gacha_result_img_01.png",
                        titleHtml: `<span class="bold_span">LINE FRIENDS SQUARE <br class="pc_only">5만 원 쇼핑 지원금</span>`,
                        productUrl: ""
                    },
                    item_2st_gacha: {
                        resultRank: "2nd",
                        badgeText: "2등",
                        imgFileName: "gacha_result_img_02.png",
                        titleHtml: `<span class="bold_span">ZO&FRIENDS ZOA <br>K-에디션 중형 인형</span>`,
                        productUrl: "https://linefriendssquare.com/products/zo-n-friends-zoa-medium-plush-k-edition-event"
                    },
                    item_3st_gacha: {
                        resultRank: "3rd",
                        badgeText: "3등",
                        imgFileName: "gacha_result_img_03.png",
                        titleHtml: `<span class="bold_span">ZO&FRIENDS ZOA <br>K-에디션 인형 키링 (선비)</span>`,
                        productUrl: "https://linefriendssquare.com/products/zo-n-friends-zoa-plush-keyring-k-edition-seonbi-event"
                    },
                    item_4st_gacha: {
                        resultRank: "4th",
                        badgeText: "4등",
                        imgFileName: "gacha_result_img_04.png",
                        titleHtml: `<span class="bold_span">라인프렌즈 레니니 <br>그리니데이 화분 미니니</span>`,
                        productUrl: "https://linefriendssquare.com/products/line-friends-greenery-lenini-plant-pot-event"
                    },
                    item_5st_gacha: {
                        resultRank: "5th",
                        badgeText: "5등",
                        imgFileName: "gacha_result_img_05.png",
                        titleHtml: `<span class="bold_span">장바구니 5천원 쿠폰 <br>(2만원 이상 구매 시)</span>`,
                        productUrl: ""
                    },
                    item_6st_gacha: {
                        resultRank: "lose",
                        badgeText: "꽝",
                        imgFileName: "gacha_result_img_06.png",
                        titleHtml: `다음 기회에!`,
                        productUrl: ""
                    },
                };

            if (copyByResultId[resultId]) {
                return copyByResultId[resultId];
            }

            // Fallback
            return copyByResultId.item_6st_gacha;
        };

        const LOTTIE_PATH =
            "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251223/js/particles_01.json";

        let particlesAnim = null;

        function getLottieLib() {
            return window.lottie || window.bodymovin || null;
        }

        function ensureParticles() {
            const lottie = getLottieLib();

            if (!lottie) return null;

            const container = $gachaPopup.find('[data-role="gacha_particles"]')[0];

            if (!container) return null;

            if (particlesAnim) return particlesAnim;

            container.innerHTML = "";

            particlesAnim = lottie.loadAnimation({
                container: container,
                renderer: "svg",
                loop: false,
                autoplay: false,
                path: LOTTIE_PATH,
            });

            return particlesAnim;
        }

        function playParticles() {
            const anim = ensureParticles();

            if (!anim) return;

            anim.stop();
            anim.goToAndPlay(0, true);
        }

        // 미리 로드
        ensureParticles();

        function openGachaPopup() {
            $gachaPopup.show();
            $("body").addClass("is-popup-open");

            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    playParticles();
                });
            });
        }

        // 복주머니에서도 호출할 수 있게 전역으로 노출
        window.openGachaPopup = openGachaPopup;

        function closeGachaPopup() {
            if (particlesAnim) {
                particlesAnim.stop();
            }

            $gachaPopup.hide();
            $("body").removeClass("is-popup-open");
        }

        // 기존 가챠 버튼 클릭 - API 연동
        $(document).on("click", ".sc_gift .gacha_btn button", async function (e) {
            e.preventDefault();

            const $thisBtn = $(this);
            const $btnWrap = $thisBtn.closest(".gacha_btn");
            const $textSpan = $thisBtn.find("span");

            const currentState = $btnWrap.attr("data-state");

            if (currentState === "soldout" || currentState === "disabled") return;

            const originalState = currentState || 'default';
            const originalHtml = $textSpan.html();

            $btnWrap.attr("data-state", "disabled");
            $textSpan.text(uiText.processing);

            if (typeof updateGachaBtnDim === 'function') {
                updateGachaBtnDim();
            }

            if ($motionWrap.length) {
                $motionWrap.addClass("is-playing");
            }

            try {
                const gachaPromise = controller.tryExchange('reward_gacha', true);
                const delayPromise = new Promise(resolve => setTimeout(resolve, 5000));

                const [response, _] = await Promise.all([gachaPromise, delayPromise]);

                if ($motionWrap.length) {
                    $motionWrap.removeClass("is-playing");
                }

                $textSpan.html(originalHtml);

                if (response && response.success) {
                    const resultData = response.data;

                    const $popupTitle = $gachaPopup.find('[data-role="result_title"]');
                    const $popupBadge = $gachaPopup.find('[data-role="result_badge"]');
                    const $popupImg = $gachaPopup.find('[data-role="result_img"] img');
                    const $btnGift = $gachaPopup.find('[data-action="go_gift"]');
                    const $btnSecondary = $gachaPopup.find('.btn_secondary');
                    const $btnCoupon = $gachaPopup.find('.btn_coupon');

                    const resultId = resultData.reward_id || resultData.result_id;
                    const uiContent = getGachaResultUiContent(resultId, resultData.description, resultData.type);

                    $gachaPopup.attr('data-result', uiContent.resultRank);

                    if ($popupTitle.length) $popupTitle.html(uiContent.titleHtml);
                    if ($popupBadge.length) $popupBadge.text(uiContent.badgeText);

                    if ($popupImg.length) {
                        const baseUrl = "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260820/img";
                        const isMobile = window.innerWidth <= 990;
                        const imgPath = isMobile ? `${baseUrl}/mo/${uiContent.imgFileName}` : `${baseUrl}/pc/${uiContent.imgFileName}`;
                        $popupImg.attr('src', imgPath);
                    }

                    // 버튼 표시 제어
                    // 1등~4등: 교환권 확인하기 (btn_secondary) 표시
                    // 5등/꽝: 쿠폰 확인하기 (btn_coupon) 표시
                    const isLowPrize = uiContent.resultRank === '5th' || uiContent.resultRank === 'lose';

                    if (isLowPrize) {
                        // 5등/꽝: 쿠폰 확인하기만 표시
                        if ($btnSecondary.length) $btnSecondary.hide();
                        if ($btnCoupon.length) $btnCoupon.show();
                    } else {
                        // 1~4등: 교환권 확인하기만 표시
                        if ($btnSecondary.length) $btnSecondary.show();
                        if ($btnCoupon.length) $btnCoupon.hide();
                    }

                    // 상품 보러가기 버튼 (2~4등용)
                    if ($btnGift.length && uiContent.productUrl) {
                        $btnGift.attr('href', uiContent.productUrl).show();
                    } else if ($btnGift.length) {
                        $btnGift.hide();
                    }

                    await fetchLatestStatus();

                    $btnWrap.attr("data-state", originalState);
                    if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();

                    openGachaPopup();
                } else {
                    $btnWrap.attr("data-state", originalState);
                    if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();
                }
            } catch (err) {
                console.error("Gacha Error:", err);
                if ($motionWrap.length) $motionWrap.removeClass("is-playing");
                $textSpan.html(originalHtml);
                $btnWrap.attr("data-state", originalState);
                if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();
            }
        });

        $(document).on("click", ".gacha_popup .popup_close", function (e) {
            e.preventDefault();
            closeGachaPopup();
        });

        $(document).on("click", ".gacha_popup .dimmed_wrap", function (e) {
            e.preventDefault();
            closeGachaPopup();
        });
    })();

    // =========================================================
    // ✅ 가챠 팝업 이미지 경로 수정
    // =========================================================
    function updateGachaResultImage() {
        const isMobile = window.innerWidth <= 990;

        $(".gacha_popup .result_img img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");

            if (!src) return;

            if (isMobile) {
                // pc → mo
                if (src.indexOf("/pc/") !== -1) {
                    $img.attr("src", src.replace("/pc/", "/mo/"));
                }
            } else {
                // mo → pc
                if (src.indexOf("/mo/") !== -1) {
                    $img.attr("src", src.replace("/mo/", "/pc/"));
                }
            }
        });
    }

    updateGachaResultImage();

    $(window).on("resize", function () {
        updateGachaResultImage();
    });
    // =========================
    // ✅ redeem_popup 여닫기
    // =========================
    // =========================================================
    // ✅ 일반 선물 교환 버튼
    // =========================================================
    document.querySelectorAll('.btn_exchange').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const targetBtn = e.currentTarget;
            const li = targetBtn.closest('li');
            const rewardId = targetBtn.dataset.rewardId;

            if (!rewardId) return;

            if (li && (li.getAttribute('data-state') === 'disabled' || li.getAttribute('data-state') === 'done')) {
                return;
            }

            const originalState = li ? li.getAttribute('data-state') : 'default';
            const originalHtml = targetBtn.innerHTML;

            if (li) li.setAttribute('data-state', 'disabled');
            targetBtn.innerHTML = `<span>${uiText.processing}</span>`;

            updateEtcButtonsDim();

            try {
                const isSuccess = await controller.tryExchange(rewardId, false);

                if (isSuccess) {
                    await fetchLatestStatus();
                } else {
                    targetBtn.innerHTML = originalHtml;
                    if (li) li.setAttribute('data-state', originalState);
                    updateEtcButtonsDim();
                }
            } catch (err) {
                console.error("Exchange Error:", err);
                targetBtn.innerHTML = originalHtml;
                if (li) li.setAttribute('data-state', originalState);
                updateEtcButtonsDim();
            }
        });
    });

    const $redeemPopup = $('.redeem_popup[data-popup="redeem_result"]');
    if ($redeemPopup.length) {
        // 닫기 (X 버튼)
        $(document).on("click", ".redeem_popup .popup_close", function (e) {
            e.preventDefault();
            closeRedeemPopup();
        });

        // 닫기 (dimmed)
        $(document).on("click", ".redeem_popup .dimmed_wrap", function (e) {
            e.preventDefault();
            closeRedeemPopup();
        });

        // 닫기 (닫기 버튼)
        $(document).on(
            "click",
            '.redeem_popup [data-action="close"]',
            function (e) {
                e.preventDefault();
                closeRedeemPopup();
            },
        );

        function closeRedeemPopup() {
            $redeemPopup.hide();
        }
    }

    // 스와이퍼
    const swiper = new Swiper(".mySwiper", {
        slidesPerView: 1,
        loop: true,

        effect: "fade",
        fadeEffect: { crossFade: true },

        autoHeight: true,

        pagination: {
            el: ".process-pagination",
            clickable: true,
        },
    });

    const swiperCommonOptions = {
        slidesPerView: "auto",
        slidesPerGroup: 1,
        spaceBetween: 12,
        speed: 400,
        autoplay: false,
        centeredSlides: false,
        initialSlide: 0,

        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,

        grabCursor: true,
        touchRatio: 1,
        resistanceRatio: 0.85,

        breakpoints: {
            991: {
                slidesPerView: 5,
                slidesPerGroup: 1,
                spaceBetween: 20,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
            },
        },
    };

    const mySwiper1 = new Swiper(".sc_shop .mySwiper1", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .top_content .swiper-button-prev",
            nextEl: ".sc_shop .top_content .swiper-button-next",
        },
    });

    const mySwiper2 = new Swiper(".sc_shop .mySwiper2", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .center_content .swiper-button-prev",
            nextEl: ".sc_shop .center_content .swiper-button-next",
        },
    });

    const mySwiper3 = new Swiper(".sc_shop .mySwiper3", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .bottom_content .swiper-button-prev",
            nextEl: ".sc_shop .bottom_content .swiper-button-next",
        },
    });

    // sc_lucky
    const luckyList = document.querySelector(".sc_lucky .content_wrap ul");

    if (luckyList) {
        const luckyObserver = new IntersectionObserver(
            ([entry], observer) => {
                if (entry.isIntersecting) {
                    luckyList.classList.add("is_active");
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.25,
            },
        );

        luckyObserver.observe(luckyList);
    }

    /// 모션
    gsap.registerPlugin(ScrollTrigger);

    // =========================================================
    // PC 스크롤 메뉴 활성화
    // =========================================================
    // =========================================================
    // 메뉴 활성화
    // =========================================================
    // =========================================================
    // PC 메뉴 활성화
    // 섹션 시작점이 화면 상단에 닿을 때 활성화
    // =========================================================
    // (function initStickyMenuActive() {
    //   var menuLinks = document.querySelectorAll(".line_wrap.pc ul a");

    //   if (!menuLinks.length) return;

    //   function clearActiveMenu() {
    //     menuLinks.forEach(function (link) {
    //       link.classList.remove("on");
    //     });
    //   }

    //   menuLinks.forEach(function (link) {
    //     var targetSelector = link.getAttribute("href");

    //     if (!targetSelector || targetSelector.charAt(0) !== "#") return;

    //     var targetSection = document.querySelector(targetSelector);

    //     if (!targetSection) return;

    //     ScrollTrigger.create({
    //       trigger: targetSection,
    //       start: "top top",
    //       end: "bottom top",

    //       onEnter: function () {
    //         if (window.isStickyMenuMoving) return;

    //         clearActiveMenu();
    //         link.classList.add("on");
    //       },

    //       onEnterBack: function () {
    //         if (window.isStickyMenuMoving) return;

    //         clearActiveMenu();
    //         link.classList.add("on");
    //       },

    //       onLeave: function () {
    //         if (window.isStickyMenuMoving) return;

    //         link.classList.remove("on");
    //       },

    //       onLeaveBack: function () {
    //         if (window.isStickyMenuMoving) return;

    //         link.classList.remove("on");
    //       },

    //       invalidateOnRefresh: true,
    //     });
    //   });
    // })();

    // =========================================================
    // PC 플로팅 메뉴 앵커 부드러운 이동
    // =========================================================
    // (function initStickyMenuAnchor() {
    //   var menuLinks = document.querySelectorAll(".line_wrap.pc ul a");
    //   var scrollEndTimer = null;

    //   if (!menuLinks.length) return;

    //   function setActiveMenu(activeLink) {
    //     menuLinks.forEach(function (link) {
    //       link.classList.toggle("on", link === activeLink);
    //     });
    //   }

    //   menuLinks.forEach(function (link) {
    //     link.addEventListener("click", function (e) {
    //       var targetSelector = link.getAttribute("href");

    //       if (!targetSelector || targetSelector.charAt(0) !== "#") return;

    //       var targetSection = document.querySelector(targetSelector);

    //       if (!targetSection) return;

    //       e.preventDefault();

    //       // 클릭 이동 중임을 표시
    //       window.isStickyMenuMoving = true;

    //       // 클릭 즉시 활성화
    //       setActiveMenu(link);

    //       var targetTop =
    //         targetSection.getBoundingClientRect().top + window.pageYOffset;

    //       window.scrollTo({
    //         top: targetTop,
    //         behavior: "smooth",
    //       });

    //       function handleScrollEnd() {
    //         clearTimeout(scrollEndTimer);

    //         scrollEndTimer = setTimeout(function () {
    //           window.isStickyMenuMoving = false;

    //           // 도착 후에도 활성화 유지
    //           setActiveMenu(link);

    //           ScrollTrigger.update();

    //           window.removeEventListener("scroll", handleScrollEnd);
    //         }, 150);
    //       }

    //       window.addEventListener("scroll", handleScrollEnd, {
    //         passive: true,
    //       });
    //     });
    //   });
    // })();

    // =========================================================
    // PC + MO 플로팅 메뉴
    // 부드러운 섹션 이동 + on 클래스 동기화
    // =========================================================
    // =========================================================
    // PC + MO 플로팅 메뉴
    // - PC / MO 메뉴 on 동기화
    // - 클릭 시 부드러운 섹션 이동
    // - PC 메뉴는 #sc_gift 진입 후 150px까지 항상 노출
    // - 그 이후 스크롤 다운 시 숨김
    // - 스크롤 업 시 다시 노출
    // - 메뉴 클릭 이동 중에는 PC 메뉴 유지
    // =========================================================
    (function initStickyMenuNavigation() {
        var menuLinks = document.querySelectorAll(
            ".line_wrap.pc ul a, .line_wrap.mo .toggle_panel ul a",
        );

        if (!menuLinks.length) return;

        var scrollEndTimer = null;
        var registeredSections = [];

        /*
         * PC 메뉴 관련 요소
         */
        var pcLineWrap = document.querySelector(".line_wrap.pc");
        var stickyStartSection = document.querySelector("#sc_gift");

        /*
         * 같은 href를 가진 PC / MO 메뉴에
         * 동시에 on 클래스 적용
         */
        function setActiveMenu(targetSelector) {
            menuLinks.forEach(function (link) {
                var linkTarget = link.getAttribute("href");

                link.classList.toggle("on", linkTarget === targetSelector);
            });
        }

        /*
         * 모든 메뉴의 on 클래스 제거
         */
        function clearActiveMenu() {
            menuLinks.forEach(function (link) {
                link.classList.remove("on");
            });
        }

        /*
         * 각 섹션별 ScrollTrigger 생성
         */
        menuLinks.forEach(function (link) {
            var targetSelector = link.getAttribute("href");

            if (!targetSelector || targetSelector.charAt(0) !== "#") return;

            /*
             * PC와 MO 메뉴의 href가 같기 때문에
             * 같은 섹션의 ScrollTrigger 중복 생성 방지
             */
            if (registeredSections.indexOf(targetSelector) !== -1) return;

            var targetSection = document.querySelector(targetSelector);

            if (!targetSection) return;

            registeredSections.push(targetSelector);

            ScrollTrigger.create({
                trigger: targetSection,
                start: "top top",
                end: "bottom top",

                onEnter: function () {
                    if (window.isStickyMenuMoving) return;

                    setActiveMenu(targetSelector);
                },

                onEnterBack: function () {
                    if (window.isStickyMenuMoving) return;

                    setActiveMenu(targetSelector);
                },

                onLeave: function () {
                    if (window.isStickyMenuMoving) return;

                    var activeLinks = document.querySelectorAll(
                        '.line_wrap a[href="' + targetSelector + '"].on',
                    );

                    activeLinks.forEach(function (activeLink) {
                        activeLink.classList.remove("on");
                    });
                },

                onLeaveBack: function () {
                    if (window.isStickyMenuMoving) return;

                    var activeLinks = document.querySelectorAll(
                        '.line_wrap a[href="' + targetSelector + '"].on',
                    );

                    activeLinks.forEach(function (activeLink) {
                        activeLink.classList.remove("on");
                    });
                },

                invalidateOnRefresh: true,
            });
        });

        /*
         * PC + MO 메뉴 클릭 시
         * 해당 섹션으로 부드럽게 이동
         */
        menuLinks.forEach(function (link) {
            link.addEventListener("click", function (event) {
                var targetSelector = link.getAttribute("href");

                if (!targetSelector || targetSelector.charAt(0) !== "#") return;

                var targetSection = document.querySelector(targetSelector);

                if (!targetSection) return;

                event.preventDefault();

                /*
                 * 클릭 스크롤 이동 중 표시
                 */
                window.isStickyMenuMoving = true;

                /*
                 * 클릭 즉시 PC / MO 같은 메뉴 활성화
                 */
                setActiveMenu(targetSelector);

                /*
                 * 메뉴 클릭 이동 중에는
                 * PC 메뉴가 숨지 않도록 노출
                 */
                if (pcLineWrap) {
                    pcLineWrap.classList.remove("is-scroll-hidden");
                }

                var targetTop =
                    targetSection.getBoundingClientRect().top + window.pageYOffset;

                function finishMenuMove() {
                    window.clearTimeout(scrollEndTimer);

                    scrollEndTimer = window.setTimeout(function () {
                        window.isStickyMenuMoving = false;

                        /*
                         * 이동 완료 후에도 on 유지
                         */
                        setActiveMenu(targetSelector);

                        ScrollTrigger.update();

                        window.removeEventListener("scroll", handleScrollEnd);
                    }, 150);
                }

                function handleScrollEnd() {
                    finishMenuMove();
                }

                window.addEventListener("scroll", handleScrollEnd, {
                    passive: true,
                });

                window.scrollTo({
                    top: targetTop,
                    behavior: "smooth",
                });

                /*
                 * 이미 해당 위치에 있어서
                 * scroll 이벤트가 발생하지 않는 경우 대비
                 */
                finishMenuMove();
            });
        });

        /*
         * 첫 메뉴 섹션보다 위로 올라가면
         * 모든 메뉴 on 제거
         */
        var firstSection = document.querySelector("#sc_gift");

        if (firstSection) {
            ScrollTrigger.create({
                trigger: firstSection,
                start: "top top",

                onLeaveBack: function () {
                    if (window.isStickyMenuMoving) return;

                    clearActiveMenu();
                },

                invalidateOnRefresh: true,
            });
        }

        /*
         * PC 플로팅 메뉴
         * #sc_gift 진입 후 일정 거리까지 항상 노출
         * 그 이후 스크롤 방향에 따라 숨김 / 노출
         */
        if (pcLineWrap && stickyStartSection) {
            var lastScrollY = window.pageYOffset;
            var scrollDirectionFrame = null;

            /*
             * 이 값보다 작은 미세 스크롤은 무시
             */
            var scrollThreshold = 5;

            /*
             * #sc_gift 시작점에서
             * 이 거리만큼 더 내려간 뒤 숨김 기능 시작
             */
            var hideStartOffset = 150;

            /*
             * 메뉴가 사라지기 시작하는
             * 문서 전체 기준 스크롤 위치 계산
             */
            function getHideStartTop() {
                var sectionTop =
                    stickyStartSection.getBoundingClientRect().top + window.pageYOffset;

                return sectionTop + hideStartOffset;
            }

            function updatePcMenuVisibility() {
                var currentScrollY = window.pageYOffset;
                var scrollDifference = currentScrollY - lastScrollY;
                var hideStartTop = getHideStartTop();

                /*
                 * 모바일에서는 숨김 기능 사용하지 않음
                 */
                if (window.innerWidth <= 990) {
                    pcLineWrap.classList.remove("is-scroll-hidden");

                    lastScrollY = currentScrollY;
                    scrollDirectionFrame = null;

                    return;
                }

                /*
                 * #sc_gift 시작점 + 150px 전까지는
                 * 항상 메뉴 노출
                 */
                if (currentScrollY < hideStartTop) {
                    pcLineWrap.classList.remove("is-scroll-hidden");

                    lastScrollY = currentScrollY;
                    scrollDirectionFrame = null;

                    return;
                }

                /*
                 * 메뉴 클릭으로 스크롤 이동 중에는
                 * 항상 메뉴 노출
                 */
                if (window.isStickyMenuMoving) {
                    pcLineWrap.classList.remove("is-scroll-hidden");

                    lastScrollY = currentScrollY;
                    scrollDirectionFrame = null;

                    return;
                }

                /*
                 * 미세한 스크롤에는 반응하지 않음
                 */
                if (Math.abs(scrollDifference) < scrollThreshold) {
                    scrollDirectionFrame = null;

                    return;
                }

                /*
                 * 스크롤 다운 시 숨김
                 */
                if (scrollDifference > 0) {
                    pcLineWrap.classList.add("is-scroll-hidden");
                }

                /*
                 * 스크롤 업 시 노출
                 */
                if (scrollDifference < 0) {
                    pcLineWrap.classList.remove("is-scroll-hidden");
                }

                lastScrollY = currentScrollY;
                scrollDirectionFrame = null;
            }

            function handlePcMenuScroll() {
                if (scrollDirectionFrame !== null) return;

                scrollDirectionFrame = window.requestAnimationFrame(
                    updatePcMenuVisibility,
                );
            }

            window.addEventListener("scroll", handlePcMenuScroll, {
                passive: true,
            });

            /*
             * 리사이즈 시 기준값과 상태 재계산
             */
            window.addEventListener("resize", function () {
                lastScrollY = window.pageYOffset;

                updatePcMenuVisibility();
            });

            /*
             * 페이지 최초 진입 시 항상 노출
             */
            pcLineWrap.classList.remove("is-scroll-hidden");

            /*
             * 새로고침 후 중간 위치에서 시작한 경우 대비
             */
            updatePcMenuVisibility();
        }
    })();

    // sc_main 모션
    const mainIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_main",
            start: "top 70%",
            once: true,
            // markers: true,
        },
        defaults: {
            ease: "power2.out",
        },
    });

    // 1️⃣ 타이틀
    mainIntroTl.to(".sc_main .main_title_wrap", {
        opacity: 1,
        y: 0,
        duration: 0.6,
    });

    // 2️⃣ 텍스트
    mainIntroTl.to(
        ".sc_main .main_txt_wrap p",
        {
            opacity: 1,
            y: 0,
            duration: 0.45,
            stagger: 0.12,
        },
        "-=0.15",
    );

    // 3️⃣ 이미지들 동시에 등장
    mainIntroTl.to(
        ".sc_main .img_box img",
        {
            opacity: 1,
            duration: 0.6,
        },
        "-=0.4", // ← 타이틀/텍스트랑 거의 동시에
    );

    // sc_coin 모션
    const coinIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_coin",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    coinIntroTl.to(
        [
            ".sc_coin .title_wrap",
            ".sc_coin .txt_wrap",
            ".sc_coin .status_wrap .flex_wrap",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    coinIntroTl.to(
        ".sc_coin .status_wrap",
        {
            opacity: 1,
            duration: 0.01,
        },
        0,
    );

    //sc_mission 모션
    const missionIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_mission",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    missionIntroTl.to(
        [
            ".sc_mission .title_wrap",
            ".sc_mission .txt_wrap",
            ".sc_mission .mission_content",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    //sc_gift 모션
    const giftIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_gift",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    giftIntroTl.to(
        [
            ".sc_gift .title_wrap",
            ".sc_gift .txt_wrap",
            ".sc_gift .gift_content",
            ".sc_gift .etc_txt",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    // 251231

    // 260605
    // 출석체크 캘린더
    // $(".attendance_days .day_item").each(function () {
    //   const $item = $(this);
    //   const state = $item.attr("data-state");

    //   if (state !== "done") return;

    //   const $dayImg = $item.find(".day_img img");
    //   const imgSrc = $dayImg.attr("src");

    //   // day_18.png → day_18_on.png
    //   const activeSrc = imgSrc.replace(".png", "_on.png");

    //   $dayImg.attr("src", activeSrc);
    // });

    // =========================================================
    // ✅ 출석체크 캘린더 이미지 노출
    // locked → 숨김
    // ready  → 노출
    // =========================================================
    $(".attendance_days .day_item").each(function () {
        var $item = $(this);
        var state = $item.attr("data-state");

        if (state === "done") {
            $item.find(".day_img").show();
        } else {
            $item.find(".day_img").hide();
        }
    });

    //  sc_main
    // sc_main anchor
    // sc_main
    // sc_main anchor
    var luckyCoinTxt = document.querySelector(".sc_main .main_txt_01");
    var luckyShopTxt = document.querySelector(".sc_main .main_txt_02");
    var luckyCoinSection = document.querySelector(".sc_coin");
    var luckyShopSection = document.querySelector(".sc_shop");

    function isMobile() {
        return window.innerWidth <= 990;
    }

    function smoothScrollTo(target) {
        if (!target) return;

        target.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }

    // 모바일 기본 상태: main_txt_01 hover 켜기
    function setMainTxtDefaultActive() {
        if (!isMobile()) return;

        if (luckyCoinTxt) luckyCoinTxt.classList.add("is_active");
        if (luckyShopTxt) luckyShopTxt.classList.remove("is_active");
    }

    setMainTxtDefaultActive();

    window.addEventListener("resize", function () {
        setMainTxtDefaultActive();
    });

    if (luckyCoinTxt) {
        luckyCoinTxt.addEventListener("click", function () {
            if (isMobile()) {
                luckyCoinTxt.classList.add("is_active");
                if (luckyShopTxt) luckyShopTxt.classList.remove("is_active");
            }

            smoothScrollTo(luckyCoinSection);
        });
    }

    if (luckyShopTxt) {
        luckyShopTxt.addEventListener("click", function () {
            if (isMobile()) {
                // 02 누르면 01은 default 상태로
                if (luckyCoinTxt) luckyCoinTxt.classList.remove("is_active");

                // 02는 hover 상태로
                luckyShopTxt.classList.add("is_active");

                setTimeout(function () {
                    smoothScrollTo(luckyShopSection);

                    // setTimeout(function () {
                    //   luckyShopTxt.classList.remove("is_active");

                    //   // 스크롤 후 다시 01 hover 상태로 돌아가게 하고 싶으면 유지
                    //   if (luckyCoinTxt) luckyCoinTxt.classList.add("is_active");
                    // }, 700);
                }, 500);
            } else {
                smoothScrollTo(luckyShopSection);
            }
        });
    }

    function initMainImageParallax() {
        var section = document.querySelector(".sc_main");
        var images = document.querySelectorAll(".sc_main .img_box img");

        if (!section || !images.length) return;

        var currentPositions = [];
        var targetPositions = [];
        var animationFrame = null;
        var isVisible = false;

        images.forEach(function () {
            currentPositions.push(0);
            targetPositions.push(0);
        });

        function clamp(value, min, max) {
            return Math.max(min, Math.min(max, value));
        }

        function updateTargetPositions() {
            var windowHeight = window.innerHeight;
            var sectionRect = section.getBoundingClientRect();

            isVisible =
                sectionRect.bottom > -windowHeight * 0.3 &&
                sectionRect.top < windowHeight * 1.3;

            if (!isVisible) return;

            /*
             * 섹션이 화면 아래쪽에 있을 때 0
             * 섹션이 화면 위쪽으로 지나갈수록 1
             */
            var progress =
                (windowHeight - sectionRect.top) / (windowHeight + sectionRect.height);

            progress = clamp(progress, 0, 1);

            images.forEach(function (image, index) {
                /*
                 * 이동 범위
                 * 첫 번째: 약 90px
                 * 두 번째: 약 120px
                 * 세 번째: 약 150px
                 */
                var moveAmount = 90 + index * 30;

                targetPositions[index] = progress * -moveAmount;
            });

            startAnimation();
        }

        function animate() {
            var shouldContinue = false;

            images.forEach(function (image, index) {
                var difference = targetPositions[index] - currentPositions[index];

                /*
                 * 숫자가 작을수록 더 천천히 부드럽게 따라옴
                 * 0.06 ~ 0.12 사이 추천
                 */
                currentPositions[index] += difference * 0.075;

                if (Math.abs(difference) > 0.05) {
                    shouldContinue = true;
                } else {
                    currentPositions[index] = targetPositions[index];
                }

                image.style.transform =
                    "translate3d(0, " + currentPositions[index] + "px, 0)";
            });

            if (shouldContinue) {
                animationFrame = window.requestAnimationFrame(animate);
            } else {
                animationFrame = null;
            }
        }

        function startAnimation() {
            if (animationFrame) return;

            animationFrame = window.requestAnimationFrame(animate);
        }

        function handleScroll() {
            updateTargetPositions();
        }

        window.addEventListener("scroll", handleScroll, {
            passive: true,
        });

        window.addEventListener("resize", updateTargetPositions);

        updateTargetPositions();
    }

    initMainImageParallax();

    // 리뷰쓰기 토스트 팝업
    var reviewToastWrap = document.querySelector(".review_toast_wrap");
    var reviewToastDimmed = document.querySelector(".review_toast_dimmed");
    var reviewButtons = document.querySelectorAll(".review_write_btn");

    if (!reviewToastWrap) return;

    var toastTimer = null;

    function openReviewToast() {
        clearTimeout(toastTimer);

        reviewToastWrap.classList.add("is_active");
        reviewToastWrap.setAttribute("aria-hidden", "false");

        toastTimer = setTimeout(function () {
            closeReviewToast();
        }, 2500);
    }

    function closeReviewToast() {
        clearTimeout(toastTimer);

        reviewToastWrap.classList.remove("is_active");
        reviewToastWrap.setAttribute("aria-hidden", "true");
    }

    reviewButtons.forEach(function (button) {
        button.addEventListener("click", function (event) {
            event.preventDefault();
            openReviewToast();
        });
    });

    if (reviewToastDimmed) {
        reviewToastDimmed.addEventListener("click", closeReviewToast);
    }

    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
            closeReviewToast();
        }
    });

    // 클라우드

    function initCloudParallax() {
        var mobileBreakpoint = 990;
        var isMobile = window.innerWidth <= mobileBreakpoint;

        var cloudWrap = document.querySelector(
            isMobile
                ? ".sc_mission .cloud_wrap.mo_only"
                : ".sc_mission .cloud_wrap.pc_only",
        );

        if (!cloudWrap) return;

        var clouds = cloudWrap.querySelectorAll("img");

        if (!clouds.length) return;

        var lastScrollY = window.pageYOffset;
        var animationFrame = null;

        var cloudOptions = [
            {
                speed: 0.07,
                ease: 0.11,
                minY: -18,
                maxY: 10,
            },
            {
                speed: 0.13,
                ease: 0.055,
                minY: -32,
                maxY: 14,
            },
            {
                speed: 0.09,
                ease: 0.085,
                minY: -24,
                maxY: 12,
            },
            {
                speed: 0.16,
                ease: 0.045,
                minY: -38,
                maxY: 16,
            },
            {
                speed: 0.11,
                ease: 0.065,
                minY: -30,
                maxY: 13,
            },
        ];

        var cloudStates = [];

        clouds.forEach(function (cloud, index) {
            cloud.style.transform = "translate3d(0, 0, 0)";

            cloudStates.push({
                currentY: 0,
                targetY: 0,
                option: cloudOptions[index] || {
                    speed: 0.08,
                    ease: 0.07,
                    minY: -25,
                    maxY: 12,
                },
            });
        });

        function clamp(value, minimum, maximum) {
            return Math.max(minimum, Math.min(maximum, value));
        }

        function isCloudSectionNearViewport() {
            var rect = cloudWrap.getBoundingClientRect();
            var windowHeight = window.innerHeight;

            return rect.bottom > -windowHeight * 0.3 && rect.top < windowHeight * 1.3;
        }

        function updateCloudTargets() {
            var currentScrollY = window.pageYOffset;
            var scrollDifference = currentScrollY - lastScrollY;

            lastScrollY = currentScrollY;

            if (!isCloudSectionNearViewport()) return;

            cloudStates.forEach(function (state) {
                state.targetY -= scrollDifference * state.option.speed;

                state.targetY = clamp(
                    state.targetY,
                    state.option.minY,
                    state.option.maxY,
                );
            });

            startAnimation();
        }

        function animate() {
            var shouldContinue = false;

            clouds.forEach(function (cloud, index) {
                var state = cloudStates[index];
                var difference = state.targetY - state.currentY;

                state.currentY += difference * state.option.ease;

                if (Math.abs(difference) > 0.05) {
                    shouldContinue = true;
                } else {
                    state.currentY = state.targetY;
                }

                cloud.style.transform = "translate3d(0, " + state.currentY + "px, 0)";
            });

            if (shouldContinue) {
                animationFrame = window.requestAnimationFrame(animate);
            } else {
                animationFrame = null;
            }
        }

        function startAnimation() {
            if (animationFrame !== null) return;

            animationFrame = window.requestAnimationFrame(animate);
        }

        window.addEventListener("scroll", updateCloudTargets, {
            passive: true,
        });
    }

    initCloudParallax();

    function initBdayEventSchedule() {
        var section = document.querySelector(".sc_bday");

        if (!section) return;

        var openButtons = section.querySelectorAll(
            ".open_button[data-open-date][data-target]",
        );

        if (!openButtons.length) return;

        var timer = null;
        var oneDay = 24 * 60 * 60 * 1000;

        /*
         * 현재 페이지가 영문 버전인지 확인
         */
        var languageContainer = section.closest(".ko_ver, .en_ver");

        var isEnglish =
            document.documentElement.classList.contains("en_ver") ||
            document.body.classList.contains("en_ver") ||
            (languageContainer && languageContainer.classList.contains("en_ver"));

        function updateEventSchedule() {
            var TEST_NOW = "2026-08-14T11:00:00+09:00";

            function getNow() {
                return TEST_NOW ? new Date(TEST_NOW) : new Date();
            }

            var now = new Date(); // 테스트 완료시 해제
            // var now = new getNow(); // 테스트 완료시 주석

            openButtons.forEach(function (openButton) {
                var openDateValue = openButton.getAttribute("data-open-date");
                var targetClass = openButton.getAttribute("data-target");
                var dDayTitle = openButton.querySelector(".title_box h3");
                var targetEvent = section.querySelector("." + targetClass);

                if (!openDateValue || !targetClass || !dDayTitle || !targetEvent) {
                    return;
                }

                var openDate = new Date(openDateValue);
                var remainingTime = openDate.getTime() - now.getTime();

                /*
                 * 공개 시간이 되었을 때
                 */
                if (remainingTime <= 0) {
                    openButton.classList.add("is_hidden");
                    openButton.setAttribute("aria-hidden", "true");

                    targetEvent.classList.add("is_open");
                    targetEvent.setAttribute("aria-hidden", "false");

                    return;
                }

                /*
                 * 아직 공개 전일 때
                 */
                var remainingDays = Math.ceil(remainingTime / oneDay);

                openButton.classList.remove("is_hidden");
                openButton.setAttribute("aria-hidden", "false");

                targetEvent.classList.remove("is_open");
                targetEvent.setAttribute("aria-hidden", "true");

                /*
                 * 국문: 공개 D-3
                 * 영문: D-3
                 */
                if (isEnglish) {
                    dDayTitle.textContent = "D-" + remainingDays;
                } else {
                    dDayTitle.textContent = "공개 D-" + remainingDays;
                }
            });
        }

        updateEventSchedule();

        /*
         * 페이지를 계속 열어둔 상태에서도
         * 공개 시간이 되면 자동으로 이벤트 오픈
         */
        timer = window.setInterval(updateEventSchedule, 1000);

        window.addEventListener("pagehide", function () {
            window.clearInterval(timer);
        });
    }

    initBdayEventSchedule();

    // 초기 실행
    (async () => {
        await fetchLatestStatus();
    })();

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") fetchLatestStatus();
    });
    window.addEventListener("focus", () => { fetchLatestStatus(); });
});

// 복주머니
(function initLuckyBagShake() {
    var topContent = document.querySelector(
        ".sc_gift .gift_content .top_content",
    );

    if (!topContent) return;

    var luckyButton = topContent.querySelector(".lucky_button button");
    var luckyList = topContent.querySelector("ul");

    if (!luckyButton || !luckyList) return;

    luckyButton.addEventListener("click", async function () {
        // data-redirect 속성이 있으면 로그인 페이지로 리다이렉트 (비로그인 상태)
        const redirectUrl = luckyButton.getAttribute('data-redirect');
        if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
        }

        // 중복 클릭 방지
        if (luckyButton.disabled) return;

        luckyButton.disabled = true;

        // 연속 실행 시 애니메이션 초기화
        luckyList.classList.remove("is_shaking");

        void luckyList.offsetWidth;

        luckyList.classList.add("is_shaking");

        const originalText = luckyButton.innerText;
        const isEnPage = document.querySelector(".january_event_container.en_ver") !== null;
        luckyButton.innerText = isEnPage ? "Processing..." : "처리중...";

        try {
            // reward_gacha 교환 API 호출 + 애니메이션 병렬 실행
            const controller = window.eventController;
            console.log('[luckyButton] Calling controller.tryExchange("reward_gacha", true)...');
            const gachaPromise = controller.tryExchange('reward_gacha', true);
            const animationPromise = new Promise(resolve => setTimeout(resolve, 800));

            const [response, _] = await Promise.all([gachaPromise, animationPromise]);

            console.log('[luckyButton] API response:', response);

            luckyList.classList.remove("is_shaking");
            luckyButton.innerText = originalText;

            if (response && response.success) {
                console.log('[luckyButton] Success! Opening gacha result popup...');
                const resultData = response.data;

                const $gachaPopup = $('.gacha_popup[data-popup="gacha_result"]');
                const $popupTitle = $gachaPopup.find('[data-role="result_title"]');
                const $popupBadge = $gachaPopup.find('[data-role="result_badge"]');
                const $popupImg = $gachaPopup.find('[data-role="result_img"] img');
                const $btnGift = $gachaPopup.find('[data-action="go_gift"]');
                const $btnSecondary = $gachaPopup.find('.btn_secondary');
                const $btnCoupon = $gachaPopup.find('.btn_coupon');

                // 가챠 결과 UI 컨텐츠 가져오기 함수
                const getGachaResultUiContent = (resultId) => {
                    const copyByResultId = isEnPage
                        ? {
                            item_1st_gacha: {
                                resultRank: "1st",
                                badgeText: "1st Place",
                                imgFileName: "gacha_result_img_01.png",
                                titleHtml: `<p class="bold_span">₩50,000 Online Store <br class="mo_only">Voucher</p>`,
                                productUrl: ""
                            },
                            item_2st_gacha: {
                                resultRank: "2nd",
                                badgeText: "2nd Place",
                                imgFileName: "gacha_result_img_02.png",
                                titleHtml: `<p class="bold_span">ZO&FRIENDS ZOA <br class="mo_only">MEDIUM PLUSH <br class="mo_only">K-EDITION</p>`,
                                productUrl: "https://linefriendssquare.com/en/products/zo-n-friends-zoa-medium-plush-k-edition-event"
                            },
                            item_3st_gacha: {
                                resultRank: "3rd",
                                badgeText: "3rd Place",
                                imgFileName: "gacha_result_img_03.png",
                                titleHtml: `<p class="bold_span">ZO&FRIENDS ZOA <br class="mo_only">PLUSH KEYRING <br class="mo_only">K-EDITION (SEONBI)</p>`,
                                productUrl: "https://linefriendssquare.com/en/products/zo-n-friends-zoa-plush-keyring-k-edition-seonbi-event"
                            },
                            item_4st_gacha: {
                                resultRank: "4th",
                                badgeText: "4th Place",
                                imgFileName: "gacha_result_img_04.png",
                                titleHtml: `<p>LINE FRIENDS <br class="mo_only">Greenery <br class="pc_only">lenini <br class="mo_only">PLANT POT</p>`,
                                productUrl: "https://linefriendssquare.com/en/products/line-friends-greenery-lenini-plant-pot-event"
                            },
                            item_5st_gacha: {
                                resultRank: "5th",
                                badgeText: "5th Place",
                                imgFileName: "gacha_result_img_05.png",
                                titleHtml: `<p>￦5,000 Cart <br class="mo_only">Coupon <br class="pc_only">(Orders <br class="mo_only">over ￦20,000)</p>`,
                                productUrl: ""
                            },
                            item_6st_gacha: {
                                resultRank: "lose",
                                badgeText: "No Prize",
                                imgFileName: "gacha_result_img_06.png",
                                titleHtml: `Better luck next time...`,
                                productUrl: ""
                            },
                        }
                        : {
                            item_1st_gacha: {
                                resultRank: "1st",
                                badgeText: "1등",
                                imgFileName: "gacha_result_img_01.png",
                                titleHtml: `<p>LINE FRIENDS SQUARE <br class="mo_only">5만 원 쇼핑 지원금</p>`,
                                productUrl: ""
                            },
                            item_2st_gacha: {
                                resultRank: "2nd",
                                badgeText: "2등",
                                imgFileName: "gacha_result_img_02.png",
                                titleHtml: `<p>ZO&FRIENDS ZOA <br class="mo_only">K-에디션 중형 인형</p>`,
                                productUrl: "https://linefriendssquare.com/products/zo-n-friends-zoa-medium-plush-k-edition-event"
                            },
                            item_3st_gacha: {
                                resultRank: "3rd",
                                badgeText: "3등",
                                imgFileName: "gacha_result_img_03.png",
                                titleHtml: `<p>ZO&FRIENDS ZOA <br class="mo_only">K-에디션 인형 키링 (선비)</p>`,
                                productUrl: "https://linefriendssquare.com/products/zo-n-friends-zoa-plush-keyring-k-edition-seonbi-event"
                            },
                            item_4st_gacha: {
                                resultRank: "4th",
                                badgeText: "4등",
                                imgFileName: "gacha_result_img_04.png",
                                titleHtml: `<p>라인프렌즈 레니니 <br class="mo_only">그리니데이 화분 미니니</p>`,
                                productUrl: "https://linefriendssquare.com/products/line-friends-greenery-lenini-plant-pot-event"
                            },
                            item_5st_gacha: {
                                resultRank: "5th",
                                badgeText: "5등",
                                imgFileName: "gacha_result_img_05.png",
                                titleHtml: `<p>장바구니 5천원 쿠폰 <br class="mo_only">(2만원 이상 구매 시)</p>`,
                                productUrl: ""
                            },
                            item_6st_gacha: {
                                resultRank: "lose",
                                badgeText: "꽝",
                                imgFileName: "gacha_result_img_06.png",
                                titleHtml: `<span class="bold_span">이번엔 아쉽지만, <br>보름달만큼 환한 행운이 곧 올 거에요!</span>`,
                                productUrl: ""
                            },
                        };

                    return copyByResultId[resultId] || copyByResultId.item_6st_gacha;
                };

                const resultId = resultData.reward_id || resultData.result_id || resultData.id;
                const uiContent = getGachaResultUiContent(resultId);

                $gachaPopup.attr('data-result', uiContent.resultRank);

                if ($popupTitle.length) $popupTitle.html(uiContent.titleHtml);
                if ($popupBadge.length) $popupBadge.text(uiContent.badgeText);

                if ($popupImg.length) {
                    const baseUrl = "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260820/img";
                    const isMobile = window.innerWidth <= 990;
                    const imgPath = isMobile ? `${baseUrl}/mo/${uiContent.imgFileName}` : `${baseUrl}/pc/${uiContent.imgFileName}`;
                    $popupImg.attr('src', imgPath);
                }

                // 버튼 표시 제어
                // 1등~4등: 교환권 확인하기 (btn_secondary) 표시
                // 5등/꽝: 쿠폰 확인하기 (btn_coupon) 표시
                const isLowPrize = uiContent.resultRank === '5th' || uiContent.resultRank === 'lose';

                if (isLowPrize) {
                    // 5등/꽝: 쿠폰 확인하기만 표시
                    if ($btnSecondary.length) $btnSecondary.hide();
                    if ($btnCoupon.length) $btnCoupon.show();
                } else {
                    // 1~4등: 교환권 확인하기만 표시
                    if ($btnSecondary.length) $btnSecondary.show();
                    if ($btnCoupon.length) $btnCoupon.hide();
                }

                // 상품 보러가기 버튼 (2~4등용)
                if ($btnGift.length && uiContent.productUrl) {
                    $btnGift.attr('href', uiContent.productUrl).show();
                } else if ($btnGift.length) {
                    $btnGift.hide();
                }

                // 최신 상태 동기화
                if (typeof window.fetchLatestStatus === 'function') {
                    await window.fetchLatestStatus();
                }

                // 복주머니 흔들림 종료 후 가챠 결과 팝업 오픈
                if (typeof window.openGachaPopup === "function") {
                    window.openGachaPopup();
                }
            } else {
                luckyList.classList.remove("is_shaking");
            }

            luckyButton.disabled = false;
        } catch (err) {
            console.error('[luckyButton] Error:', err);
            luckyList.classList.remove("is_shaking");
            luckyButton.innerText = originalText;
            luckyButton.disabled = false;
        }
    });
})();

// =========================================================
// ✅ 버튼 텍스트 변경 로직은 updateMissionUI() 함수 내부로 이동됨
// 260824 주석 요구사항 모두 적용 완료
// =========================================================

// popup_close
(function updateGachaCloseImage() {
    var $gachaPopup = $(".gacha_popup[data-popup='gacha_result']");
    if (!$gachaPopup.length) return;

    var result = $gachaPopup.attr("data-result");
    var $closeImg = $gachaPopup.find(".popup_close img");

    if (result === "2nd" || result === "3rd" || result === "4th") {
        $closeImg.attr(
            "src",
            "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260820/img/pc/popup_close2.png",
        );
    } else {
        $closeImg.attr(
            "src",
            "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260820/img/pc/popup_close.png",
        );
    }
})();
